// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as ModelMapping from './configuration.modelMapping';
import * as ModelConstraint from './configuration.modelConstraint';

export function isEntityModel(source: any): source is IEntity {
    // Method Implementation: simple
    return source.modelName === 'Entity';
}

export function isConstraintModelTemplateModel(source: any): source is IConstraintModelTemplate {
    // Method Implementation: simple
    return source.modelName === 'ConstraintModelTemplate';
}

export function isChildTemplateModel(source: any): source is IChildTemplate {
    // Method Implementation: simple
    return source.modelName === 'ChildTemplate';
}

export function isInlineChildTemplateModel(source: any): source is IInlineChildTemplate {
    // Method Implementation: simple
    return source.modelName === 'InlineChildTemplate';
}

export function isReferenceChildTemplateModel(source: any): source is IReferenceChildTemplate {
    // Method Implementation: simple
    return source.modelName === 'ReferenceChildTemplate';
}

export function isLinkedMapperModel(source: any): source is ILinkedMapper {
    // Method Implementation: simple
    return source.modelName === 'LinkedMapper';
}

export function isLinkedConstraintModelModel(source: any): source is ILinkedConstraintModel {
    // Method Implementation: simple
    return source.modelName === 'LinkedConstraintModel';
}

export function isChildModel(source: any): source is IChild {
    // Method Implementation: simple
    return source.modelName === 'Child';
}

export function isInlineChildModel(source: any): source is IInlineChild {
    // Method Implementation: simple
    return source.modelName === 'InlineChild';
}

export function isReferenceChildModel(source: any): source is IReferenceChild {
    // Method Implementation: simple
    return source.modelName === 'ReferenceChild';
}

export function isChildVariableModel(source: any): source is IChildVariable {
    // Method Implementation: simple
    return source.modelName === 'ChildVariable';
}

export function isVariableModel(source: any): source is IVariable {
    // Method Implementation: simple
    return source.modelName === 'Variable';
}

export function isStringVariableModel(source: any): source is IStringVariable {
    // Method Implementation: simple
    return source.modelName === 'StringVariable';
}

export function isNumericVariableModel(source: any): source is INumericVariable {
    // Method Implementation: simple
    return source.modelName === 'NumericVariable';
}

export function isBooleanVariableModel(source: any): source is IBooleanVariable {
    // Method Implementation: simple
    return source.modelName === 'BooleanVariable';
}

export function isFieldModel(source: any): source is IField {
    // Method Implementation: simple
    return source.modelName === 'Field';
}

export function isStringFieldModel(source: any): source is IStringField {
    // Method Implementation: simple
    return source.modelName === 'StringField';
}

export function isNumericFieldModel(source: any): source is INumericField {
    // Method Implementation: simple
    return source.modelName === 'NumericField';
}

export function isBooleanFieldModel(source: any): source is IBooleanField {
    // Method Implementation: simple
    return source.modelName === 'BooleanField';
}

export function isVariableOptionModel(source: any): source is IVariableOption {
    // Method Implementation: simple
    return source.modelName === 'VariableOption';
}

export function isStringVariableOptionModel(source: any): source is IStringVariableOption {
    // Method Implementation: simple
    return source.modelName === 'StringVariableOption';
}

export function isNumericVariableOptionModel(source: any): source is INumericVariableOption {
    // Method Implementation: simple
    return source.modelName === 'NumericVariableOption';
}

export function isFieldOptionModel(source: any): source is IFieldOption {
    // Method Implementation: simple
    return source.modelName === 'FieldOption';
}

export function isStringFieldOptionModel(source: any): source is IStringFieldOption {
    // Method Implementation: simple
    return source.modelName === 'StringFieldOption';
}

export function isNumericFieldOptionModel(source: any): source is INumericFieldOption {
    // Method Implementation: simple
    return source.modelName === 'NumericFieldOption';
}

export function isValidatorModel(source: any): source is IValidator {
    // Method Implementation: simple
    return source.modelName === 'Validator';
}

export function isTransientActionModel(source: any): source is ITransientAction {
    // Method Implementation: simple
    return source.modelName === 'TransientAction';
}

export interface EntityZodSchemaShape {
    name?: string;

    businessUnit?: string;

    mappers?: LinkedMapperZodSchemaShape[];

    constraintModels?: LinkedConstraintModelZodSchemaShape[];

    variables?: VariableZodSchemaShape[];

    fields?: FieldZodSchemaShape[];

    validators?: ValidatorZodSchemaShape[];

    children?: ChildZodSchemaShape[];

    childTemplates?: ChildTemplateZodSchemaShape[];

    assignmentGroups?: any;

    constraintModelTemplates?: ConstraintModelTemplateZodSchemaShape[];
}

export interface ConstraintModelTemplateZodSchemaShape {
    name?: string;

    priority?: string;
}

export interface ChildTemplateZodSchemaShape {
    childTemplateType?: string;

    name?: string;

    mappingCondition?: string;

    decoupleCondition?: string;

    mapper?: ModelMapping.MapperZodSchemaShape;

    variables?: ChildVariableZodSchemaShape[];
}

export interface InlineChildTemplateZodSchemaShape {
    childTemplateType?: string;

    name?: string;

    mappingCondition?: string;

    decoupleCondition?: string;

    mapper?: ModelMapping.MapperZodSchemaShape;

    variables?: ChildVariableZodSchemaShape[];

    entity?: EntityZodSchemaShape;
}

export interface ReferenceChildTemplateZodSchemaShape {
    childTemplateType?: string;

    name?: string;

    mappingCondition?: string;

    decoupleCondition?: string;

    mapper?: ModelMapping.MapperZodSchemaShape;

    variables?: ChildVariableZodSchemaShape[];

    entity?: string;
}

export interface LinkedMapperZodSchemaShape {
    name?: string;

    mapper?: ModelMapping.MapperZodSchemaShape;
}

export interface LinkedConstraintModelZodSchemaShape {
    name?: string;

    priority?: string;

    enabled?: string;

    constraintModel?: ModelConstraint.ConstraintModelZodSchemaShape;
}

export interface ChildZodSchemaShape {
    childType?: string;

    name?: string;

    type?: string;

    mapper?: ModelMapping.MapperZodSchemaShape;

    variables?: ChildVariableZodSchemaShape[];
}

export interface InlineChildZodSchemaShape {
    childType?: string;

    name?: string;

    type?: string;

    mapper?: ModelMapping.MapperZodSchemaShape;

    variables?: ChildVariableZodSchemaShape[];

    entity?: EntityZodSchemaShape;
}

export interface ReferenceChildZodSchemaShape {
    childType?: string;

    name?: string;

    type?: string;

    mapper?: ModelMapping.MapperZodSchemaShape;

    variables?: ChildVariableZodSchemaShape[];

    entity?: string;
}

export interface ChildVariableZodSchemaShape {
    name?: string;

    value?: string;
}

export interface VariableZodSchemaShape {
    variableType?: string;

    name?: string;

    value?: string;

    required?: string;

    strict?: string;

    validators?: ValidatorZodSchemaShape[];

    assignmentGroup?: string;
}

export interface StringVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    value?: string;

    required?: string;

    strict?: string;

    validators?: ValidatorZodSchemaShape[];

    assignmentGroup?: string;

    options?: StringVariableOptionZodSchemaShape[];

    multi?: boolean;
}

export interface NumericVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    value?: string;

    required?: string;

    strict?: string;

    validators?: ValidatorZodSchemaShape[];

    assignmentGroup?: string;

    precision?: number;

    min?: string;

    max?: string;

    options?: NumericVariableOptionZodSchemaShape[];

    multi?: boolean;
}

export interface BooleanVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    value?: string;

    required?: string;

    strict?: string;

    validators?: ValidatorZodSchemaShape[];

    assignmentGroup?: string;
}

export interface FieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];
}

export interface StringFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];

    options?: StringFieldOptionZodSchemaShape[];
}

export interface NumericFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];

    precision?: number;

    options?: NumericFieldOptionZodSchemaShape[];
}

export interface BooleanFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];
}

export interface VariableOptionZodSchemaShape {
    variableOptionType?: string;

    name?: string;

    onSelectedActions?: TransientActionZodSchemaShape[];

    applicable?: string;
}

export interface StringVariableOptionZodSchemaShape {
    variableOptionType?: string;

    name?: string;

    onSelectedActions?: TransientActionZodSchemaShape[];

    applicable?: string;

    value?: string;
}

export interface NumericVariableOptionZodSchemaShape {
    variableOptionType?: string;

    name?: string;

    onSelectedActions?: TransientActionZodSchemaShape[];

    applicable?: string;

    value?: number;
}

export interface FieldOptionZodSchemaShape {
    fieldOptionType?: string;

    name?: string;

    applicable?: string;
}

export interface StringFieldOptionZodSchemaShape {
    fieldOptionType?: string;

    name?: string;

    applicable?: string;

    value?: string;
}

export interface NumericFieldOptionZodSchemaShape {
    fieldOptionType?: string;

    name?: string;

    applicable?: string;

    value?: number;
}

export interface ValidatorZodSchemaShape {
    name?: string;

    condition?: string;

    messageKey?: string;

    type?: string;
}

export interface TransientActionZodSchemaShape {
    name?: string;

    type?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const EntityZodSchema: Zod.ZodSchema<EntityZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    businessUnit: Zod.string().optional(),
    mappers: Zod.array(LinkedMapperZodSchema).optional(),
    constraintModels: Zod.array(LinkedConstraintModelZodSchema).optional(),
    variables: Zod.array(VariableZodSchema).optional(),
    fields: Zod.array(FieldZodSchema).optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    children: Zod.array(ChildZodSchema).optional(),
    childTemplates: Zod.array(ChildTemplateZodSchema).optional(),
    assignmentGroups: Zod.any().optional(),
    constraintModelTemplates: Zod.array(ConstraintModelTemplateZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConstraintModelTemplateZodSchema: Zod.ZodSchema<ConstraintModelTemplateZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    priority: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ChildTemplateZodSchema: Zod.ZodSchema<ChildTemplateZodSchemaShape> = Zod.lazy(() => Zod.object({
    childTemplateType: Zod.string(),
    name: Zod.string().optional(),
    mappingCondition: Zod.string().optional(),
    decoupleCondition: Zod.string().optional(),
    mapper: ModelMapping.MapperZodSchema.optional(),
    variables: Zod.array(ChildVariableZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const InlineChildTemplateZodSchema: Zod.ZodSchema<InlineChildTemplateZodSchemaShape> = Zod.lazy(() => Zod.object({
    childTemplateType: Zod.string(),
    name: Zod.string().optional(),
    mappingCondition: Zod.string().optional(),
    decoupleCondition: Zod.string().optional(),
    mapper: ModelMapping.MapperZodSchema.optional(),
    variables: Zod.array(ChildVariableZodSchema).optional(),
    entity: EntityZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ReferenceChildTemplateZodSchema: Zod.ZodSchema<ReferenceChildTemplateZodSchemaShape> = Zod.lazy(() => Zod.object({
    childTemplateType: Zod.string(),
    name: Zod.string().optional(),
    mappingCondition: Zod.string().optional(),
    decoupleCondition: Zod.string().optional(),
    mapper: ModelMapping.MapperZodSchema.optional(),
    variables: Zod.array(ChildVariableZodSchema).optional(),
    entity: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const LinkedMapperZodSchema: Zod.ZodSchema<LinkedMapperZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    mapper: ModelMapping.MapperZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const LinkedConstraintModelZodSchema: Zod.ZodSchema<LinkedConstraintModelZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    priority: Zod.string().optional(),
    enabled: Zod.string().optional(),
    constraintModel: ModelConstraint.ConstraintModelZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ChildZodSchema: Zod.ZodSchema<ChildZodSchemaShape> = Zod.lazy(() => Zod.object({
    childType: Zod.string(),
    name: Zod.string().optional(),
    type: Zod.string().optional(),
    mapper: ModelMapping.MapperZodSchema.optional(),
    variables: Zod.array(ChildVariableZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const InlineChildZodSchema: Zod.ZodSchema<InlineChildZodSchemaShape> = Zod.lazy(() => Zod.object({
    childType: Zod.string(),
    name: Zod.string().optional(),
    type: Zod.string().optional(),
    mapper: ModelMapping.MapperZodSchema.optional(),
    variables: Zod.array(ChildVariableZodSchema).optional(),
    entity: EntityZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ReferenceChildZodSchema: Zod.ZodSchema<ReferenceChildZodSchemaShape> = Zod.lazy(() => Zod.object({
    childType: Zod.string(),
    name: Zod.string().optional(),
    type: Zod.string().optional(),
    mapper: ModelMapping.MapperZodSchema.optional(),
    variables: Zod.array(ChildVariableZodSchema).optional(),
    entity: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ChildVariableZodSchema: Zod.ZodSchema<ChildVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const VariableZodSchema: Zod.ZodSchema<VariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    assignmentGroup: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringVariableZodSchema: Zod.ZodSchema<StringVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    assignmentGroup: Zod.string().optional(),
    options: Zod.array(StringVariableOptionZodSchema).optional(),
    multi: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericVariableZodSchema: Zod.ZodSchema<NumericVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    assignmentGroup: Zod.string().optional(),
    precision: Zod.number().optional(),
    min: Zod.string().optional(),
    max: Zod.string().optional(),
    options: Zod.array(NumericVariableOptionZodSchema).optional(),
    multi: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanVariableZodSchema: Zod.ZodSchema<BooleanVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    assignmentGroup: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const FieldZodSchema: Zod.ZodSchema<FieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringFieldZodSchema: Zod.ZodSchema<StringFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    options: Zod.array(StringFieldOptionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericFieldZodSchema: Zod.ZodSchema<NumericFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    precision: Zod.number().optional(),
    options: Zod.array(NumericFieldOptionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanFieldZodSchema: Zod.ZodSchema<BooleanFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const VariableOptionZodSchema: Zod.ZodSchema<VariableOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableOptionType: Zod.string(),
    name: Zod.string().optional(),
    onSelectedActions: Zod.array(TransientActionZodSchema).optional(),
    applicable: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringVariableOptionZodSchema: Zod.ZodSchema<StringVariableOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableOptionType: Zod.string(),
    name: Zod.string().optional(),
    onSelectedActions: Zod.array(TransientActionZodSchema).optional(),
    applicable: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericVariableOptionZodSchema: Zod.ZodSchema<NumericVariableOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableOptionType: Zod.string(),
    name: Zod.string().optional(),
    onSelectedActions: Zod.array(TransientActionZodSchema).optional(),
    applicable: Zod.string().optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const FieldOptionZodSchema: Zod.ZodSchema<FieldOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldOptionType: Zod.string(),
    name: Zod.string().optional(),
    applicable: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringFieldOptionZodSchema: Zod.ZodSchema<StringFieldOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldOptionType: Zod.string(),
    name: Zod.string().optional(),
    applicable: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericFieldOptionZodSchema: Zod.ZodSchema<NumericFieldOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldOptionType: Zod.string(),
    name: Zod.string().optional(),
    applicable: Zod.string().optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ValidatorZodSchema: Zod.ZodSchema<ValidatorZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    condition: Zod.string().optional(),
    messageKey: Zod.string().optional(),
    type: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const TransientActionZodSchema: Zod.ZodSchema<TransientActionZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    type: Zod.string().optional()
}).nonstrict());

export interface IEntity extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly businessUnit: string;

    readonly mappers: LinkedMapper[];

    readonly constraintModels: LinkedConstraintModel[];

    readonly variables: AVariable[];

    readonly fields: AField[];

    readonly validators: Validator[];

    readonly children: AChild[];

    readonly childTemplates: AChildTemplate[];

    readonly assignmentGroups: Record<string, string>;

    readonly constraintModelTemplates: ConstraintModelTemplate[];

    readonly isNameDefined: boolean;

    readonly isBusinessUnitDefined: boolean;

    readonly isMappersDefined: boolean;

    readonly isConstraintModelsDefined: boolean;

    readonly isVariablesDefined: boolean;

    readonly isFieldsDefined: boolean;

    readonly isValidatorsDefined: boolean;

    readonly isChildrenDefined: boolean;

    readonly isChildTemplatesDefined: boolean;

    readonly isAssignmentGroupsDefined: boolean;

    readonly isConstraintModelTemplatesDefined: boolean;
}

export interface IConstraintModelTemplate extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly priority: string;

    readonly isNameDefined: boolean;

    readonly isPriorityDefined: boolean;
}

export interface IChildTemplate extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly mappingCondition: string;

    readonly decoupleCondition: string;

    readonly mapper: ModelMapping.Mapper;

    readonly variables: ChildVariable[];

    readonly isNameDefined: boolean;

    readonly isMappingConditionDefined: boolean;

    readonly isDecoupleConditionDefined: boolean;

    readonly isMapperDefined: boolean;

    readonly isVariablesDefined: boolean;
}

export interface IInlineChildTemplate extends IChildTemplate, Wellspring.IModel {
    readonly modelName: string;

    readonly entity: Entity;

    readonly isEntityDefined: boolean;
}

export interface IReferenceChildTemplate extends IChildTemplate, Wellspring.IModel {
    readonly modelName: string;

    readonly entity: string;

    readonly isEntityDefined: boolean;
}

export interface ILinkedMapper extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly mapper: ModelMapping.Mapper;

    readonly isNameDefined: boolean;

    readonly isMapperDefined: boolean;
}

export interface ILinkedConstraintModel extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly priority: string;

    readonly enabled: string;

    readonly constraintModel: ModelConstraint.ConstraintModel;

    readonly isNameDefined: boolean;

    readonly isPriorityDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isConstraintModelDefined: boolean;
}

export interface IChild extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly type: string;

    readonly mapper: ModelMapping.Mapper;

    readonly variables: ChildVariable[];

    readonly isNameDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isMapperDefined: boolean;

    readonly isVariablesDefined: boolean;
}

export interface IInlineChild extends IChild, Wellspring.IModel {
    readonly modelName: string;

    readonly entity: Entity;

    readonly isEntityDefined: boolean;
}

export interface IReferenceChild extends IChild, Wellspring.IModel {
    readonly modelName: string;

    readonly entity: string;

    readonly isEntityDefined: boolean;
}

export interface IChildVariable extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly value: string;

    readonly isNameDefined: boolean;

    readonly isValueDefined: boolean;
}

export interface IVariable extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly value: string;

    readonly required: string;

    readonly strict: string;

    readonly validators: Validator[];

    readonly assignmentGroup: string;

    readonly isNameDefined: boolean;

    readonly isValueDefined: boolean;

    readonly isRequiredDefined: boolean;

    readonly isStrictDefined: boolean;

    readonly isValidatorsDefined: boolean;

    readonly isAssignmentGroupDefined: boolean;
}

export interface IStringVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;

    readonly options: StringVariableOption[];

    readonly multi: boolean;

    readonly isOptionsDefined: boolean;

    readonly isMultiDefined: boolean;
}

export interface INumericVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;

    readonly precision: number;

    readonly min: string;

    readonly max: string;

    readonly options: NumericVariableOption[];

    readonly multi: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;

    readonly isOptionsDefined: boolean;

    readonly isMultiDefined: boolean;
}

export interface IBooleanVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;
}

export interface IField extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly source: string;

    readonly required: string;

    readonly validators: Validator[];

    readonly isNameDefined: boolean;

    readonly isSourceDefined: boolean;

    readonly isRequiredDefined: boolean;

    readonly isValidatorsDefined: boolean;
}

export interface IStringField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly options: StringFieldOption[];

    readonly isOptionsDefined: boolean;
}

export interface INumericField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly precision: number;

    readonly options: NumericFieldOption[];

    readonly isPrecisionDefined: boolean;

    readonly isOptionsDefined: boolean;
}

export interface IBooleanField extends IField, Wellspring.IModel {
    readonly modelName: string;
}

export interface IVariableOption extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly onSelectedActions: TransientAction[];

    readonly applicable: string;

    readonly isNameDefined: boolean;

    readonly isOnSelectedActionsDefined: boolean;

    readonly isApplicableDefined: boolean;
}

export interface IStringVariableOption extends IVariableOption, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericVariableOption extends IVariableOption, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IFieldOption extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly applicable: string;

    readonly isNameDefined: boolean;

    readonly isApplicableDefined: boolean;
}

export interface IStringFieldOption extends IFieldOption, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericFieldOption extends IFieldOption, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IValidator extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly condition: string;

    readonly messageKey: string;

    readonly type: string;

    readonly isNameDefined: boolean;

    readonly isConditionDefined: boolean;

    readonly isMessageKeyDefined: boolean;

    readonly isTypeDefined: boolean;
}

export interface ITransientAction extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly type: string;

    readonly isNameDefined: boolean;

    readonly isTypeDefined: boolean;
}

export class Entity implements IEntity {
    public static readonly modelName: string = 'Entity';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'businessUnit', 'mappers', 'constraintModels', 'variables', 'fields', 'validators', 'children', 'childTemplates', 'assignmentGroups', 'constraintModelTemplates' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Entity.modelName;
    }

    private _name: string;

    private _businessUnit: string;

    private _mappers: LinkedMapper[];

    private _constraintModels: LinkedConstraintModel[];

    private _variables: AVariable[];

    private _fields: AField[];

    private _validators: Validator[];

    private _children: AChild[];

    private _childTemplates: AChildTemplate[];

    private _assignmentGroups: Record<string, string>;

    private _constraintModelTemplates: ConstraintModelTemplate[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get businessUnit(): string {
        // Method Implementation: simple
        return this._businessUnit;
    }

    public get mappers(): LinkedMapper[] {
        // Method Implementation: simple
        return this._mappers;
    }

    public get constraintModels(): LinkedConstraintModel[] {
        // Method Implementation: simple
        return this._constraintModels;
    }

    public get variables(): AVariable[] {
        // Method Implementation: simple
        return this._variables;
    }

    public get fields(): AField[] {
        // Method Implementation: simple
        return this._fields;
    }

    public get validators(): Validator[] {
        // Method Implementation: simple
        return this._validators;
    }

    public get children(): AChild[] {
        // Method Implementation: simple
        return this._children;
    }

    public get childTemplates(): AChildTemplate[] {
        // Method Implementation: simple
        return this._childTemplates;
    }

    public get assignmentGroups(): Record<string, string> {
        // Method Implementation: simple
        return this._assignmentGroups;
    }

    public get constraintModelTemplates(): ConstraintModelTemplate[] {
        // Method Implementation: simple
        return this._constraintModelTemplates;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set businessUnit(value: string) {
        // Method Implementation: simple
        this._businessUnit = value;
        this._isBusinessUnitDefined = true;
    }

    public set mappers(value: LinkedMapper[]) {
        // Method Implementation: simple
        this._mappers = value;
        this._isMappersDefined = true;
    }

    public set constraintModels(value: LinkedConstraintModel[]) {
        // Method Implementation: simple
        this._constraintModels = value;
        this._isConstraintModelsDefined = true;
    }

    public set variables(value: AVariable[]) {
        // Method Implementation: simple
        this._variables = value;
        this._isVariablesDefined = true;
    }

    public set fields(value: AField[]) {
        // Method Implementation: simple
        this._fields = value;
        this._isFieldsDefined = true;
    }

    public set validators(value: Validator[]) {
        // Method Implementation: simple
        this._validators = value;
        this._isValidatorsDefined = true;
    }

    public set children(value: AChild[]) {
        // Method Implementation: simple
        this._children = value;
        this._isChildrenDefined = true;
    }

    public set childTemplates(value: AChildTemplate[]) {
        // Method Implementation: simple
        this._childTemplates = value;
        this._isChildTemplatesDefined = true;
    }

    public set assignmentGroups(value: Record<string, string>) {
        // Method Implementation: simple
        this._assignmentGroups = value;
        this._isAssignmentGroupsDefined = true;
    }

    public set constraintModelTemplates(value: ConstraintModelTemplate[]) {
        // Method Implementation: simple
        this._constraintModelTemplates = value;
        this._isConstraintModelTemplatesDefined = true;
    }

    private _isNameDefined: boolean;

    private _isBusinessUnitDefined: boolean;

    private _isMappersDefined: boolean;

    private _isConstraintModelsDefined: boolean;

    private _isVariablesDefined: boolean;

    private _isFieldsDefined: boolean;

    private _isValidatorsDefined: boolean;

    private _isChildrenDefined: boolean;

    private _isChildTemplatesDefined: boolean;

    private _isAssignmentGroupsDefined: boolean;

    private _isConstraintModelTemplatesDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isBusinessUnitDefined(): boolean {
        // Method Implementation: simple
        return this._isBusinessUnitDefined;
    }

    public get isMappersDefined(): boolean {
        // Method Implementation: simple
        return this._isMappersDefined;
    }

    public get isConstraintModelsDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintModelsDefined;
    }

    public get isVariablesDefined(): boolean {
        // Method Implementation: simple
        return this._isVariablesDefined;
    }

    public get isFieldsDefined(): boolean {
        // Method Implementation: simple
        return this._isFieldsDefined;
    }

    public get isValidatorsDefined(): boolean {
        // Method Implementation: simple
        return this._isValidatorsDefined;
    }

    public get isChildrenDefined(): boolean {
        // Method Implementation: simple
        return this._isChildrenDefined;
    }

    public get isChildTemplatesDefined(): boolean {
        // Method Implementation: simple
        return this._isChildTemplatesDefined;
    }

    public get isAssignmentGroupsDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignmentGroupsDefined;
    }

    public get isConstraintModelTemplatesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintModelTemplatesDefined;
    }

    public constructor();
    public constructor(source: IEntity);
    public constructor(source: Partial<Pick<Entity, 'name' | 'businessUnit' | 'mappers' | 'constraintModels' | 'variables' | 'fields' | 'validators' | 'children' | 'childTemplates' | 'assignmentGroups' | 'constraintModelTemplates'>>);
    public constructor(source?: IEntity | Partial<Pick<Entity, 'name' | 'businessUnit' | 'mappers' | 'constraintModels' | 'variables' | 'fields' | 'validators' | 'children' | 'childTemplates' | 'assignmentGroups' | 'constraintModelTemplates'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isBusinessUnitDefined) {
                    this._businessUnit = source.businessUnit;
                    this._isBusinessUnitDefined = true;
                }
                else {
                    this._businessUnit = '';
                    this._isBusinessUnitDefined = false;
                }
                
                if (source.isMappersDefined) {
                    this._mappers = source.mappers;
                    this._isMappersDefined = true;
                }
                else {
                    this._mappers = [];
                    this._isMappersDefined = true;
                }
                
                if (source.isConstraintModelsDefined) {
                    this._constraintModels = source.constraintModels;
                    this._isConstraintModelsDefined = true;
                }
                else {
                    this._constraintModels = [];
                    this._isConstraintModelsDefined = true;
                }
                
                if (source.isVariablesDefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
                
                if (source.isFieldsDefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
                
                if (source.isValidatorsDefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.isChildrenDefined) {
                    this._children = source.children;
                    this._isChildrenDefined = true;
                }
                else {
                    this._children = [];
                    this._isChildrenDefined = true;
                }
                
                if (source.isChildTemplatesDefined) {
                    this._childTemplates = source.childTemplates;
                    this._isChildTemplatesDefined = true;
                }
                else {
                    this._childTemplates = [];
                    this._isChildTemplatesDefined = true;
                }
                
                if (source.isAssignmentGroupsDefined) {
                    this._assignmentGroups = source.assignmentGroups;
                    this._isAssignmentGroupsDefined = true;
                }
                else {
                    this._assignmentGroups = {};
                    this._isAssignmentGroupsDefined = true;
                }
                
                if (source.isConstraintModelTemplatesDefined) {
                    this._constraintModelTemplates = source.constraintModelTemplates;
                    this._isConstraintModelTemplatesDefined = true;
                }
                else {
                    this._constraintModelTemplates = [];
                    this._isConstraintModelTemplatesDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.businessUnit !== undefined) {
                    this._businessUnit = source.businessUnit;
                    this._isBusinessUnitDefined = true;
                }
                else {
                    this._businessUnit = '';
                    this._isBusinessUnitDefined = false;
                }
                
                if (source.mappers !== undefined) {
                    this._mappers = source.mappers;
                    this._isMappersDefined = true;
                }
                else {
                    this._mappers = [];
                    this._isMappersDefined = true;
                }
                
                if (source.constraintModels !== undefined) {
                    this._constraintModels = source.constraintModels;
                    this._isConstraintModelsDefined = true;
                }
                else {
                    this._constraintModels = [];
                    this._isConstraintModelsDefined = true;
                }
                
                if (source.variables !== undefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
                
                if (source.fields !== undefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
                
                if (source.validators !== undefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.children !== undefined) {
                    this._children = source.children;
                    this._isChildrenDefined = true;
                }
                else {
                    this._children = [];
                    this._isChildrenDefined = true;
                }
                
                if (source.childTemplates !== undefined) {
                    this._childTemplates = source.childTemplates;
                    this._isChildTemplatesDefined = true;
                }
                else {
                    this._childTemplates = [];
                    this._isChildTemplatesDefined = true;
                }
                
                if (source.assignmentGroups !== undefined) {
                    this._assignmentGroups = source.assignmentGroups;
                    this._isAssignmentGroupsDefined = true;
                }
                else {
                    this._assignmentGroups = {};
                    this._isAssignmentGroupsDefined = true;
                }
                
                if (source.constraintModelTemplates !== undefined) {
                    this._constraintModelTemplates = source.constraintModelTemplates;
                    this._isConstraintModelTemplatesDefined = true;
                }
                else {
                    this._constraintModelTemplates = [];
                    this._isConstraintModelTemplatesDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._businessUnit = '';
            this._isBusinessUnitDefined = false;
        
            this._mappers = [];
            this._isMappersDefined = true;
        
            this._constraintModels = [];
            this._isConstraintModelsDefined = true;
        
            this._variables = [];
            this._isVariablesDefined = true;
        
            this._fields = [];
            this._isFieldsDefined = true;
        
            this._validators = [];
            this._isValidatorsDefined = true;
        
            this._children = [];
            this._isChildrenDefined = true;
        
            this._childTemplates = [];
            this._isChildTemplatesDefined = true;
        
            this._assignmentGroups = {};
            this._isAssignmentGroupsDefined = true;
        
            this._constraintModelTemplates = [];
            this._isConstraintModelTemplatesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Entity.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined)
            definedProperties.push('businessUnit');
        if (this.isMappersDefined && this.mappers !== undefined)
            definedProperties.push('mappers');
        if (this.isConstraintModelsDefined && this.constraintModels !== undefined)
            definedProperties.push('constraintModels');
        if (this.isVariablesDefined && this.variables !== undefined)
            definedProperties.push('variables');
        if (this.isFieldsDefined && this.fields !== undefined)
            definedProperties.push('fields');
        if (this.isValidatorsDefined && this.validators !== undefined)
            definedProperties.push('validators');
        if (this.isChildrenDefined && this.children !== undefined)
            definedProperties.push('children');
        if (this.isChildTemplatesDefined && this.childTemplates !== undefined)
            definedProperties.push('childTemplates');
        if (this.isAssignmentGroupsDefined && this.assignmentGroups !== undefined)
            definedProperties.push('assignmentGroups');
        if (this.isConstraintModelTemplatesDefined && this.constraintModelTemplates !== undefined)
            definedProperties.push('constraintModelTemplates');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'businessUnit':
                return this.businessUnit;
            case 'mappers':
                return this.mappers;
            case 'constraintModels':
                return this.constraintModels;
            case 'variables':
                return this.variables;
            case 'fields':
                return this.fields;
            case 'validators':
                return this.validators;
            case 'children':
                return this.children;
            case 'childTemplates':
                return this.childTemplates;
            case 'assignmentGroups':
                return this.assignmentGroups;
            case 'constraintModelTemplates':
                return this.constraintModelTemplates;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined) {
            if (this.businessUnit !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('businessUnit', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.businessUnit);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('businessUnit', 'null'));
            }
        }
        
        if (this.isMappersDefined && this.mappers !== undefined) {
            if (this.mappers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.mappers.length));
                for (let i = 0; i < this.mappers.length; i++) {
                    const item = this.mappers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappers', 'null'));
            }
        }
        
        if (this.isConstraintModelsDefined && this.constraintModels !== undefined) {
            if (this.constraintModels !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModels', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintModels.length));
                for (let i = 0; i < this.constraintModels.length; i++) {
                    const item = this.constraintModels[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModels', 'null'));
            }
        }
        
        if (this.isVariablesDefined && this.variables !== undefined) {
            if (this.variables !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.variables.length));
                for (let i = 0; i < this.variables.length; i++) {
                    const item = this.variables[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'null'));
            }
        }
        
        if (this.isFieldsDefined && this.fields !== undefined) {
            if (this.fields !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.fields.length));
                for (let i = 0; i < this.fields.length; i++) {
                    const item = this.fields[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'null'));
            }
        }
        
        if (this.isValidatorsDefined && this.validators !== undefined) {
            if (this.validators !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validators.length));
                for (let i = 0; i < this.validators.length; i++) {
                    const item = this.validators[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'null'));
            }
        }
        
        if (this.isChildrenDefined && this.children !== undefined) {
            if (this.children !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('children', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.children.length));
                for (let i = 0; i < this.children.length; i++) {
                    const item = this.children[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('children', 'null'));
            }
        }
        
        if (this.isChildTemplatesDefined && this.childTemplates !== undefined) {
            if (this.childTemplates !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('childTemplates', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.childTemplates.length));
                for (let i = 0; i < this.childTemplates.length; i++) {
                    const item = this.childTemplates[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('childTemplates', 'null'));
            }
        }
        
        if (this.isAssignmentGroupsDefined && this.assignmentGroups !== undefined) {
            if (this.assignmentGroups !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentGroups', 'dictionary'));
        
                // Implementation: axon/write/dictionary
                writer.writeDictionaryHeader(new Axon.DictionaryHeader(_.keys(this.assignmentGroups).length));
                for (let key in this.assignmentGroups) {
                    const dictionaryValue = this.assignmentGroups[key];
                
                    writer.writeDictionaryItemHeader(new Axon.DictionaryItemHeader('string', dictionaryValue !== null ? 'string' : 'null'));
                
                    // Key Implementation: axon/write/string
                    writer.writeStringValue(key);
                
                    if (dictionaryValue !== null) {
                        // Implementation: axon/write/string
                        writer.writeStringValue(dictionaryValue);
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentGroups', 'null'));
            }
        }
        
        if (this.isConstraintModelTemplatesDefined && this.constraintModelTemplates !== undefined) {
            if (this.constraintModelTemplates !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModelTemplates', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintModelTemplates.length));
                for (let i = 0; i < this.constraintModelTemplates.length; i++) {
                    const item = this.constraintModelTemplates[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModelTemplates', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'businessUnit':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.businessUnit = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'businessUnit' cannot be null`);
                    this.businessUnit = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'businessUnit'`);
                }
        
                return true;
            case 'mappers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.mappers = [];
                
                    let item: LinkedMapper = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === LinkedMapper.modelName) {
                                item = new LinkedMapper();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${LinkedMapper.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'mappers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'mappers'`);
                        }
                
                        this.mappers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'mappers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mappers'`);
                }
        
                return true;
            case 'constraintModels':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintModels = [];
                
                    let item: LinkedConstraintModel = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === LinkedConstraintModel.modelName) {
                                item = new LinkedConstraintModel();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${LinkedConstraintModel.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintModels' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintModels'`);
                        }
                
                        this.constraintModels.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintModels' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintModels'`);
                }
        
                return true;
            case 'variables':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.variables = [];
                
                    let item: AVariable = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'variables' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'variables'`);
                        }
                
                        this.variables.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'variables' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variables'`);
                }
        
                return true;
            case 'fields':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.fields = [];
                
                    let item: AField = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'fields' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'fields'`);
                        }
                
                        this.fields.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'fields' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fields'`);
                }
        
                return true;
            case 'validators':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validators = [];
                
                    let item: Validator = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Validator.modelName) {
                                item = new Validator();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Validator.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validators' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validators'`);
                        }
                
                        this.validators.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validators' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validators'`);
                }
        
                return true;
            case 'children':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.children = [];
                
                    let item: AChild = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'children' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'children'`);
                        }
                
                        this.children.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'children' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'children'`);
                }
        
                return true;
            case 'childTemplates':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.childTemplates = [];
                
                    let item: AChildTemplate = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'childTemplates' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'childTemplates'`);
                        }
                
                        this.childTemplates.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'childTemplates' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'childTemplates'`);
                }
        
                return true;
            case 'assignmentGroups':
                // Implementation: axon/readProperty/dictionary
                if (propertyHeader.type === 'dictionary') {
                    const dictionaryHeader = reader.readDictionaryHeader();
                
                    this.assignmentGroups = {};
                
                    for (let i = 0; i < dictionaryHeader.recordCount; i++) {
                        let key: string = <any>undefined;
                        let value: string = <any>undefined;
                
                        const dictionaryItemHeader = reader.readDictionaryItemHeader();
                
                        // Key Implementation: string
                        if (dictionaryItemHeader.keyType === 'string') {
                            key = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.keyType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'assignmentGroups' cannot be null`);
                            key = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.keyType}' for property 'assignmentGroups'`);
                        }
                
                        // Value Implementation: string
                        if (dictionaryItemHeader.valueType === 'string') {
                            value = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.valueType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'assignmentGroups' cannot be null`);
                            value = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.valueType}' for property 'assignmentGroups'`);
                        }
                
                        this.assignmentGroups[key] = value;
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'assignmentGroups' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignmentGroups'`);
                }
        
                return true;
            case 'constraintModelTemplates':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintModelTemplates = [];
                
                    let item: ConstraintModelTemplate = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConstraintModelTemplate.modelName) {
                                item = new ConstraintModelTemplate();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConstraintModelTemplate.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintModelTemplates' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintModelTemplates'`);
                        }
                
                        this.constraintModelTemplates.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintModelTemplates' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintModelTemplates'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // businessUnit
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.businessUnit === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.businessUnit);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // mappers
        if (this.isMappersDefined && this.mappers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.mappers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.mappers.length);
            
                for (const item of this.mappers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintModels
        if (this.isConstraintModelsDefined && this.constraintModels !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintModels === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintModels.length);
            
                for (const item of this.constraintModels) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // variables
        if (this.isVariablesDefined && this.variables !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.variables === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.variables.length);
            
                for (const item of this.variables) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof StringVariable)
                            writer.writeShortValue(0);
                        else if (item instanceof NumericVariable)
                            writer.writeShortValue(1);
                        else if (item instanceof BooleanVariable)
                            writer.writeShortValue(2);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // fields
        if (this.isFieldsDefined && this.fields !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.fields === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.fields.length);
            
                for (const item of this.fields) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof StringField)
                            writer.writeShortValue(0);
                        else if (item instanceof NumericField)
                            writer.writeShortValue(1);
                        else if (item instanceof BooleanField)
                            writer.writeShortValue(2);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validators
        if (this.isValidatorsDefined && this.validators !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validators === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validators.length);
            
                for (const item of this.validators) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // children
        if (this.isChildrenDefined && this.children !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.children === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.children.length);
            
                for (const item of this.children) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof InlineChild)
                            writer.writeShortValue(0);
                        else if (item instanceof ReferenceChild)
                            writer.writeShortValue(1);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // childTemplates
        if (this.isChildTemplatesDefined && this.childTemplates !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.childTemplates === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.childTemplates.length);
            
                for (const item of this.childTemplates) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof InlineChildTemplate)
                            writer.writeShortValue(0);
                        else if (item instanceof ReferenceChildTemplate)
                            writer.writeShortValue(1);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignmentGroups
        if (this.isAssignmentGroupsDefined && this.assignmentGroups !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/dictionary
            if (this.assignmentGroups === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(_.keys(this.assignmentGroups).length);
            
                for (let key in this.assignmentGroups) {
                    // Key Implementation: string
                    if (key === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(key);
                    }
            
                    // Value Implementation: string
                    if (this.assignmentGroups[key] === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(this.assignmentGroups[key]);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintModelTemplates
        if (this.isConstraintModelTemplatesDefined && this.constraintModelTemplates !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintModelTemplates === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintModelTemplates.length);
            
                for (const item of this.constraintModelTemplates) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // businessUnit
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.businessUnit = <any>null;
                    break;
                case 1:
                    this.businessUnit = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // mappers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: LinkedMapper;
            
                    this.mappers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new LinkedMapper();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.mappers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintModels
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: LinkedConstraintModel;
            
                    this.constraintModels = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new LinkedConstraintModel();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintModels.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // variables
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AVariable;
            
                    this.variables = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new StringVariable();
                                        break;
                                    case 1:
                                        item = new NumericVariable();
                                        break;
                                    case 2:
                                        item = new BooleanVariable();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.variables.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // fields
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AField;
            
                    this.fields = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new StringField();
                                        break;
                                    case 1:
                                        item = new NumericField();
                                        break;
                                    case 2:
                                        item = new BooleanField();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.fields.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validators
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Validator;
            
                    this.validators = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Validator();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validators.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // children
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AChild;
            
                    this.children = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new InlineChild();
                                        break;
                                    case 1:
                                        item = new ReferenceChild();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.children.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // childTemplates
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AChildTemplate;
            
                    this.childTemplates = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new InlineChildTemplate();
                                        break;
                                    case 1:
                                        item = new ReferenceChildTemplate();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.childTemplates.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // assignmentGroups
        if (reader.readBooleanValue())
        {
            // Implementation: dictionary
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null dictionaries');
                    break;
                case 1:
                    let key: string;
                    let value: string;
            
                    this.assignmentGroups = {};
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Key Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                key = <any>null;
                                break;
                            case 1:
                                key = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        // Value Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                value = <any>null;
                                break;
                            case 1:
                                value = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.assignmentGroups[key] = value;
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintModelTemplates
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConstraintModelTemplate;
            
                    this.constraintModelTemplates = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConstraintModelTemplate();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintModelTemplates.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Entity {
        // Method Implementation: simple
        const schema = EntityZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityZodSchemaShape): Entity {
        // Method Implementation: parseZodSchema
        return new Entity({
            name: schema.name,
            businessUnit: schema.businessUnit,
            mappers: schema.mappers?.map(i => LinkedMapper.parseZodSchema(i)),
            constraintModels: schema.constraintModels?.map(i => LinkedConstraintModel.parseZodSchema(i)),
            variables: schema.variables?.map(i => AVariable.parseZodSchema(i)),
            fields: schema.fields?.map(i => AField.parseZodSchema(i)),
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            children: schema.children?.map(i => AChild.parseZodSchema(i)),
            childTemplates: schema.childTemplates?.map(i => AChildTemplate.parseZodSchema(i)),
            assignmentGroups: schema.assignmentGroups,
            constraintModelTemplates: schema.constraintModelTemplates?.map(i => ConstraintModelTemplate.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            businessUnit: this.isBusinessUnitDefined ? ((this.businessUnit === null ? undefined : this.businessUnit)) : undefined,
            mappers: this.isMappersDefined ? (this.mappers?.map(item => item)) : undefined,
            constraintModels: this.isConstraintModelsDefined ? (this.constraintModels?.map(item => item)) : undefined,
            variables: this.isVariablesDefined ? (this.variables?.map(item => item)) : undefined,
            fields: this.isFieldsDefined ? (this.fields?.map(item => item)) : undefined,
            validators: this.isValidatorsDefined ? (this.validators?.map(item => item)) : undefined,
            children: this.isChildrenDefined ? (this.children?.map(item => item)) : undefined,
            childTemplates: this.isChildTemplatesDefined ? (this.childTemplates?.map(item => item)) : undefined,
            assignmentGroups: this.isAssignmentGroupsDefined ? (this.assignmentGroups) : undefined,
            constraintModelTemplates: this.isConstraintModelTemplatesDefined ? (this.constraintModelTemplates?.map(item => item)) : undefined,
        };
    }

    public clone(): Entity {
        // Method Implementation: clone
        return new Entity({
            name: this.isNameDefined ? this.name : undefined,
            businessUnit: this.isBusinessUnitDefined ? this.businessUnit : undefined,
            mappers: this.isMappersDefined ? this.mappers?.map(i => i.clone()) : undefined,
            constraintModels: this.isConstraintModelsDefined ? this.constraintModels?.map(i => i.clone()) : undefined,
            variables: this.isVariablesDefined ? this.variables?.map(i => i.clone()) : undefined,
            fields: this.isFieldsDefined ? this.fields?.map(i => i.clone()) : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            children: this.isChildrenDefined ? this.children?.map(i => i.clone()) : undefined,
            childTemplates: this.isChildTemplatesDefined ? this.childTemplates?.map(i => i.clone()) : undefined,
            assignmentGroups: this.isAssignmentGroupsDefined ? this.assignmentGroups : undefined,
            constraintModelTemplates: this.isConstraintModelTemplatesDefined ? this.constraintModelTemplates?.map(i => i.clone()) : undefined
        });
    }
}

export class ConstraintModelTemplate implements IConstraintModelTemplate {
    public static readonly modelName: string = 'ConstraintModelTemplate';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'priority' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConstraintModelTemplate.modelName;
    }

    private _name: string;

    private _priority: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get priority(): string {
        // Method Implementation: simple
        return this._priority;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set priority(value: string) {
        // Method Implementation: simple
        this._priority = value;
        this._isPriorityDefined = true;
    }

    private _isNameDefined: boolean;

    private _isPriorityDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isPriorityDefined(): boolean {
        // Method Implementation: simple
        return this._isPriorityDefined;
    }

    public constructor();
    public constructor(source: IConstraintModelTemplate);
    public constructor(source: Partial<Pick<ConstraintModelTemplate, 'name' | 'priority'>>);
    public constructor(source?: IConstraintModelTemplate | Partial<Pick<ConstraintModelTemplate, 'name' | 'priority'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConstraintModelTemplateModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isPriorityDefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = '';
                    this._isPriorityDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.priority !== undefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = '';
                    this._isPriorityDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._priority = '';
            this._isPriorityDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConstraintModelTemplate.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isPriorityDefined && this.priority !== undefined)
            definedProperties.push('priority');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'priority':
                return this.priority;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isPriorityDefined && this.priority !== undefined) {
            if (this.priority !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.priority);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'priority':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.priority = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'priority' cannot be null`);
                    this.priority = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'priority'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // priority
        if (this.isPriorityDefined && this.priority !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.priority === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.priority);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // priority
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.priority = <any>null;
                    break;
                case 1:
                    this.priority = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConstraintModelTemplate {
        // Method Implementation: simple
        const schema = ConstraintModelTemplateZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConstraintModelTemplateZodSchemaShape): ConstraintModelTemplate {
        // Method Implementation: parseZodSchema
        return new ConstraintModelTemplate({
            name: schema.name,
            priority: schema.priority
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            priority: this.isPriorityDefined ? ((this.priority === null ? undefined : this.priority)) : undefined,
        };
    }

    public clone(): ConstraintModelTemplate {
        // Method Implementation: clone
        return new ConstraintModelTemplate({
            name: this.isNameDefined ? this.name : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined
        });
    }
}

export abstract class AChildTemplate implements IChildTemplate {
    public static readonly modelName: string = 'ChildTemplate';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'mappingCondition', 'decoupleCondition', 'mapper', 'variables' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AChildTemplate.modelName;
    }

    private _name: string;

    private _mappingCondition: string;

    private _decoupleCondition: string;

    private _mapper: ModelMapping.Mapper;

    private _variables: ChildVariable[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get mappingCondition(): string {
        // Method Implementation: simple
        return this._mappingCondition;
    }

    public get decoupleCondition(): string {
        // Method Implementation: simple
        return this._decoupleCondition;
    }

    public get mapper(): ModelMapping.Mapper {
        // Method Implementation: simple
        return this._mapper;
    }

    public get variables(): ChildVariable[] {
        // Method Implementation: simple
        return this._variables;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set mappingCondition(value: string) {
        // Method Implementation: simple
        this._mappingCondition = value;
        this._isMappingConditionDefined = true;
    }

    public set decoupleCondition(value: string) {
        // Method Implementation: simple
        this._decoupleCondition = value;
        this._isDecoupleConditionDefined = true;
    }

    public set mapper(value: ModelMapping.Mapper) {
        // Method Implementation: simple
        this._mapper = value;
        this._isMapperDefined = true;
    }

    public set variables(value: ChildVariable[]) {
        // Method Implementation: simple
        this._variables = value;
        this._isVariablesDefined = true;
    }

    private _isNameDefined: boolean;

    private _isMappingConditionDefined: boolean;

    private _isDecoupleConditionDefined: boolean;

    private _isMapperDefined: boolean;

    private _isVariablesDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isMappingConditionDefined(): boolean {
        // Method Implementation: simple
        return this._isMappingConditionDefined;
    }

    public get isDecoupleConditionDefined(): boolean {
        // Method Implementation: simple
        return this._isDecoupleConditionDefined;
    }

    public get isMapperDefined(): boolean {
        // Method Implementation: simple
        return this._isMapperDefined;
    }

    public get isVariablesDefined(): boolean {
        // Method Implementation: simple
        return this._isVariablesDefined;
    }

    public constructor();
    public constructor(source: IChildTemplate);
    public constructor(source: Partial<Pick<AChildTemplate, 'name' | 'mappingCondition' | 'decoupleCondition' | 'mapper' | 'variables'>>);
    public constructor(source?: IChildTemplate | Partial<Pick<AChildTemplate, 'name' | 'mappingCondition' | 'decoupleCondition' | 'mapper' | 'variables'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isChildTemplateModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isMappingConditionDefined) {
                    this._mappingCondition = source.mappingCondition;
                    this._isMappingConditionDefined = true;
                }
                else {
                    this._mappingCondition = '';
                    this._isMappingConditionDefined = false;
                }
                
                if (source.isDecoupleConditionDefined) {
                    this._decoupleCondition = source.decoupleCondition;
                    this._isDecoupleConditionDefined = true;
                }
                else {
                    this._decoupleCondition = '';
                    this._isDecoupleConditionDefined = false;
                }
                
                if (source.isMapperDefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
                
                if (source.isVariablesDefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.mappingCondition !== undefined) {
                    this._mappingCondition = source.mappingCondition;
                    this._isMappingConditionDefined = true;
                }
                else {
                    this._mappingCondition = '';
                    this._isMappingConditionDefined = false;
                }
                
                if (source.decoupleCondition !== undefined) {
                    this._decoupleCondition = source.decoupleCondition;
                    this._isDecoupleConditionDefined = true;
                }
                else {
                    this._decoupleCondition = '';
                    this._isDecoupleConditionDefined = false;
                }
                
                if (source.mapper !== undefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
                
                if (source.variables !== undefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._mappingCondition = '';
            this._isMappingConditionDefined = false;
        
            this._decoupleCondition = '';
            this._isDecoupleConditionDefined = false;
        
            this._mapper = new ModelMapping.Mapper();
            this._isMapperDefined = true;
        
            this._variables = [];
            this._isVariablesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AChildTemplate.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isMappingConditionDefined && this.mappingCondition !== undefined)
            definedProperties.push('mappingCondition');
        if (this.isDecoupleConditionDefined && this.decoupleCondition !== undefined)
            definedProperties.push('decoupleCondition');
        if (this.isMapperDefined && this.mapper !== undefined)
            definedProperties.push('mapper');
        if (this.isVariablesDefined && this.variables !== undefined)
            definedProperties.push('variables');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'mappingCondition':
                return this.mappingCondition;
            case 'decoupleCondition':
                return this.decoupleCondition;
            case 'mapper':
                return this.mapper;
            case 'variables':
                return this.variables;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isMappingConditionDefined && this.mappingCondition !== undefined) {
            if (this.mappingCondition !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappingCondition', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.mappingCondition);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappingCondition', 'null'));
            }
        }
        
        if (this.isDecoupleConditionDefined && this.decoupleCondition !== undefined) {
            if (this.decoupleCondition !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('decoupleCondition', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.decoupleCondition);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('decoupleCondition', 'null'));
            }
        }
        
        if (this.isMapperDefined && this.mapper !== undefined) {
            if (this.mapper !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.mapper.getModelName(), this.mapper.getDefinedPropertyNames().length));
                this.mapper.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'null'));
            }
        }
        
        if (this.isVariablesDefined && this.variables !== undefined) {
            if (this.variables !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.variables.length));
                for (let i = 0; i < this.variables.length; i++) {
                    const item = this.variables[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'mappingCondition':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.mappingCondition = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'mappingCondition' cannot be null`);
                    this.mappingCondition = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mappingCondition'`);
                }
        
                return true;
            case 'decoupleCondition':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.decoupleCondition = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'decoupleCondition' cannot be null`);
                    this.decoupleCondition = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'decoupleCondition'`);
                }
        
                return true;
            case 'mapper':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ModelMapping.Mapper.modelName) {
                        this.mapper = new ModelMapping.Mapper();
                        this.mapper.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelMapping.Mapper.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'mapper' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mapper'`);
                }
        
                return true;
            case 'variables':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.variables = [];
                
                    let item: ChildVariable = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ChildVariable.modelName) {
                                item = new ChildVariable();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ChildVariable.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'variables' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'variables'`);
                        }
                
                        this.variables.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'variables' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variables'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // mappingCondition
        if (this.isMappingConditionDefined && this.mappingCondition !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.mappingCondition === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.mappingCondition);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // decoupleCondition
        if (this.isDecoupleConditionDefined && this.decoupleCondition !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.decoupleCondition === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.decoupleCondition);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // mapper
        if (this.isMapperDefined && this.mapper !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.mapper === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.mapper.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // variables
        if (this.isVariablesDefined && this.variables !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.variables === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.variables.length);
            
                for (const item of this.variables) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // mappingCondition
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.mappingCondition = <any>null;
                    break;
                case 1:
                    this.mappingCondition = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // decoupleCondition
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.decoupleCondition = <any>null;
                    break;
                case 1:
                    this.decoupleCondition = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // mapper
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.mapper = new ModelMapping.Mapper();
                    this.mapper.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // variables
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ChildVariable;
            
                    this.variables = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ChildVariable();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.variables.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AChildTemplate {
        // Method Implementation: simple
        const schema = ChildTemplateZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ChildTemplateZodSchemaShape): AChildTemplate {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.childTemplateType)
        {
            case 'inlineChildTemplate':
            case 'inline':
                return InlineChildTemplate.parse(schema);
            case 'referenceChildTemplate':
            case 'reference':
                return ReferenceChildTemplate.parse(schema);
            default:
                throw new Error(`childTemplateType of ${ schema.childTemplateType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            mappingCondition: this.isMappingConditionDefined ? ((this.mappingCondition === null ? undefined : this.mappingCondition)) : undefined,
            decoupleCondition: this.isDecoupleConditionDefined ? ((this.decoupleCondition === null ? undefined : this.decoupleCondition)) : undefined,
            mapper: this.isMapperDefined ? (this.mapper) : undefined,
            variables: this.isVariablesDefined ? (this.variables?.map(item => item)) : undefined,
        };
    }

    public abstract clone(): AChildTemplate;
}

export class InlineChildTemplate extends AChildTemplate implements IInlineChildTemplate {
    public static readonly modelName: string = 'InlineChildTemplate';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entity' ];

    public get modelName(): string {
        // Method Implementation: simple
        return InlineChildTemplate.modelName;
    }

    private _entity: Entity;

    public get entity(): Entity {
        // Method Implementation: simple
        return this._entity;
    }

    public set entity(value: Entity) {
        // Method Implementation: simple
        this._entity = value;
        this._isEntityDefined = true;
    }

    private _isEntityDefined: boolean;

    public get isEntityDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityDefined;
    }

    public constructor();
    public constructor(source: IInlineChildTemplate);
    public constructor(source: Partial<Pick<InlineChildTemplate, 'entity' | 'name' | 'mappingCondition' | 'decoupleCondition' | 'mapper' | 'variables'>>);
    public constructor(source?: IInlineChildTemplate | Partial<Pick<InlineChildTemplate, 'entity' | 'name' | 'mappingCondition' | 'decoupleCondition' | 'mapper' | 'variables'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isInlineChildTemplateModel(source)) {
                if (source.isEntityDefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = new Entity();
                    this._isEntityDefined = true;
                }
            }
            else {
                if (source.entity !== undefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = new Entity();
                    this._isEntityDefined = true;
                }
            }
        }
        else {
            super();
        
            this._entity = new Entity();
            this._isEntityDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...InlineChildTemplate.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isEntityDefined && this.entity !== undefined)
            definedProperties.push('entity');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entity':
                return this.entity;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isEntityDefined && this.entity !== undefined) {
            if (this.entity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.entity.getModelName(), this.entity.getDefinedPropertyNames().length));
                this.entity.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entity':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === Entity.modelName) {
                        this.entity = new Entity();
                        this.entity.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Entity.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'entity' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entity'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // entity
        if (this.isEntityDefined && this.entity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.entity === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.entity.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // entity
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.entity = new Entity();
                    this.entity.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): InlineChildTemplate {
        // Method Implementation: simple
        const schema = InlineChildTemplateZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: InlineChildTemplateZodSchemaShape): InlineChildTemplate {
        // Method Implementation: parseZodSchema
        return new InlineChildTemplate({
            name: schema.name,
            mappingCondition: schema.mappingCondition,
            decoupleCondition: schema.decoupleCondition,
            mapper: schema.mapper && ModelMapping.Mapper.parseZodSchema(schema.mapper),
            variables: schema.variables?.map(i => ChildVariable.parseZodSchema(i)),
            entity: schema.entity && Entity.parseZodSchema(schema.entity)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            childTemplateType: 'inline',
            ...super.toJSON(),
            entity: this.isEntityDefined ? (this.entity) : undefined,
        };
    }

    public clone(): InlineChildTemplate {
        // Method Implementation: clone
        return new InlineChildTemplate({
            name: this.isNameDefined ? this.name : undefined,
            mappingCondition: this.isMappingConditionDefined ? this.mappingCondition : undefined,
            decoupleCondition: this.isDecoupleConditionDefined ? this.decoupleCondition : undefined,
            mapper: this.isMapperDefined ? this.mapper.clone() : undefined,
            variables: this.isVariablesDefined ? this.variables?.map(i => i.clone()) : undefined,
            entity: this.isEntityDefined ? this.entity.clone() : undefined
        });
    }
}

export class ReferenceChildTemplate extends AChildTemplate implements IReferenceChildTemplate {
    public static readonly modelName: string = 'ReferenceChildTemplate';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entity' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ReferenceChildTemplate.modelName;
    }

    private _entity: string;

    public get entity(): string {
        // Method Implementation: simple
        return this._entity;
    }

    public set entity(value: string) {
        // Method Implementation: simple
        this._entity = value;
        this._isEntityDefined = true;
    }

    private _isEntityDefined: boolean;

    public get isEntityDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityDefined;
    }

    public constructor();
    public constructor(source: IReferenceChildTemplate);
    public constructor(source: Partial<Pick<ReferenceChildTemplate, 'entity' | 'name' | 'mappingCondition' | 'decoupleCondition' | 'mapper' | 'variables'>>);
    public constructor(source?: IReferenceChildTemplate | Partial<Pick<ReferenceChildTemplate, 'entity' | 'name' | 'mappingCondition' | 'decoupleCondition' | 'mapper' | 'variables'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isReferenceChildTemplateModel(source)) {
                if (source.isEntityDefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = '';
                    this._isEntityDefined = false;
                }
            }
            else {
                if (source.entity !== undefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = '';
                    this._isEntityDefined = false;
                }
            }
        }
        else {
            super();
        
            this._entity = '';
            this._isEntityDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...ReferenceChildTemplate.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isEntityDefined && this.entity !== undefined)
            definedProperties.push('entity');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entity':
                return this.entity;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isEntityDefined && this.entity !== undefined) {
            if (this.entity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entity);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entity':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entity = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entity' cannot be null`);
                    this.entity = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entity'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // entity
        if (this.isEntityDefined && this.entity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entity === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entity);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // entity
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entity = <any>null;
                    break;
                case 1:
                    this.entity = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ReferenceChildTemplate {
        // Method Implementation: simple
        const schema = ReferenceChildTemplateZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ReferenceChildTemplateZodSchemaShape): ReferenceChildTemplate {
        // Method Implementation: parseZodSchema
        return new ReferenceChildTemplate({
            name: schema.name,
            mappingCondition: schema.mappingCondition,
            decoupleCondition: schema.decoupleCondition,
            mapper: schema.mapper && ModelMapping.Mapper.parseZodSchema(schema.mapper),
            variables: schema.variables?.map(i => ChildVariable.parseZodSchema(i)),
            entity: schema.entity
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            childTemplateType: 'reference',
            ...super.toJSON(),
            entity: this.isEntityDefined ? ((this.entity === null ? undefined : this.entity)) : undefined,
        };
    }

    public clone(): ReferenceChildTemplate {
        // Method Implementation: clone
        return new ReferenceChildTemplate({
            name: this.isNameDefined ? this.name : undefined,
            mappingCondition: this.isMappingConditionDefined ? this.mappingCondition : undefined,
            decoupleCondition: this.isDecoupleConditionDefined ? this.decoupleCondition : undefined,
            mapper: this.isMapperDefined ? this.mapper.clone() : undefined,
            variables: this.isVariablesDefined ? this.variables?.map(i => i.clone()) : undefined,
            entity: this.isEntityDefined ? this.entity : undefined
        });
    }
}

export class LinkedMapper implements ILinkedMapper {
    public static readonly modelName: string = 'LinkedMapper';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'mapper' ];

    public get modelName(): string {
        // Method Implementation: simple
        return LinkedMapper.modelName;
    }

    private _name: string;

    private _mapper: ModelMapping.Mapper;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get mapper(): ModelMapping.Mapper {
        // Method Implementation: simple
        return this._mapper;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set mapper(value: ModelMapping.Mapper) {
        // Method Implementation: simple
        this._mapper = value;
        this._isMapperDefined = true;
    }

    private _isNameDefined: boolean;

    private _isMapperDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isMapperDefined(): boolean {
        // Method Implementation: simple
        return this._isMapperDefined;
    }

    public constructor();
    public constructor(source: ILinkedMapper);
    public constructor(source: Partial<Pick<LinkedMapper, 'name' | 'mapper'>>);
    public constructor(source?: ILinkedMapper | Partial<Pick<LinkedMapper, 'name' | 'mapper'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isLinkedMapperModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isMapperDefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.mapper !== undefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._mapper = new ModelMapping.Mapper();
            this._isMapperDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return LinkedMapper.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isMapperDefined && this.mapper !== undefined)
            definedProperties.push('mapper');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'mapper':
                return this.mapper;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isMapperDefined && this.mapper !== undefined) {
            if (this.mapper !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.mapper.getModelName(), this.mapper.getDefinedPropertyNames().length));
                this.mapper.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'mapper':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ModelMapping.Mapper.modelName) {
                        this.mapper = new ModelMapping.Mapper();
                        this.mapper.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelMapping.Mapper.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'mapper' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mapper'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // mapper
        if (this.isMapperDefined && this.mapper !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.mapper === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.mapper.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // mapper
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.mapper = new ModelMapping.Mapper();
                    this.mapper.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): LinkedMapper {
        // Method Implementation: simple
        const schema = LinkedMapperZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: LinkedMapperZodSchemaShape): LinkedMapper {
        // Method Implementation: parseZodSchema
        return new LinkedMapper({
            name: schema.name,
            mapper: schema.mapper && ModelMapping.Mapper.parseZodSchema(schema.mapper)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            mapper: this.isMapperDefined ? (this.mapper) : undefined,
        };
    }

    public clone(): LinkedMapper {
        // Method Implementation: clone
        return new LinkedMapper({
            name: this.isNameDefined ? this.name : undefined,
            mapper: this.isMapperDefined ? this.mapper.clone() : undefined
        });
    }
}

export class LinkedConstraintModel implements ILinkedConstraintModel {
    public static readonly modelName: string = 'LinkedConstraintModel';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'priority', 'enabled', 'constraintModel' ];

    public get modelName(): string {
        // Method Implementation: simple
        return LinkedConstraintModel.modelName;
    }

    private _name: string;

    private _priority: string;

    private _enabled: string;

    private _constraintModel: ModelConstraint.ConstraintModel;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get priority(): string {
        // Method Implementation: simple
        return this._priority;
    }

    public get enabled(): string {
        // Method Implementation: simple
        return this._enabled;
    }

    public get constraintModel(): ModelConstraint.ConstraintModel {
        // Method Implementation: simple
        return this._constraintModel;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set priority(value: string) {
        // Method Implementation: simple
        this._priority = value;
        this._isPriorityDefined = true;
    }

    public set enabled(value: string) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set constraintModel(value: ModelConstraint.ConstraintModel) {
        // Method Implementation: simple
        this._constraintModel = value;
        this._isConstraintModelDefined = true;
    }

    private _isNameDefined: boolean;

    private _isPriorityDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isConstraintModelDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isPriorityDefined(): boolean {
        // Method Implementation: simple
        return this._isPriorityDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isConstraintModelDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintModelDefined;
    }

    public constructor();
    public constructor(source: ILinkedConstraintModel);
    public constructor(source: Partial<Pick<LinkedConstraintModel, 'name' | 'priority' | 'enabled' | 'constraintModel'>>);
    public constructor(source?: ILinkedConstraintModel | Partial<Pick<LinkedConstraintModel, 'name' | 'priority' | 'enabled' | 'constraintModel'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isLinkedConstraintModelModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isPriorityDefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = '';
                    this._isPriorityDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.isConstraintModelDefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = new ModelConstraint.ConstraintModel();
                    this._isConstraintModelDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.priority !== undefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = '';
                    this._isPriorityDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.constraintModel !== undefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = new ModelConstraint.ConstraintModel();
                    this._isConstraintModelDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._priority = '';
            this._isPriorityDefined = false;
        
            this._enabled = '';
            this._isEnabledDefined = false;
        
            this._constraintModel = new ModelConstraint.ConstraintModel();
            this._isConstraintModelDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return LinkedConstraintModel.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isPriorityDefined && this.priority !== undefined)
            definedProperties.push('priority');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isConstraintModelDefined && this.constraintModel !== undefined)
            definedProperties.push('constraintModel');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'priority':
                return this.priority;
            case 'enabled':
                return this.enabled;
            case 'constraintModel':
                return this.constraintModel;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isPriorityDefined && this.priority !== undefined) {
            if (this.priority !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.priority);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            if (this.constraintModel !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.constraintModel.getModelName(), this.constraintModel.getDefinedPropertyNames().length));
                this.constraintModel.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'priority':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.priority = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'priority' cannot be null`);
                    this.priority = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'priority'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.enabled = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'enabled' cannot be null`);
                    this.enabled = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'constraintModel':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ModelConstraint.ConstraintModel.modelName) {
                        this.constraintModel = new ModelConstraint.ConstraintModel();
                        this.constraintModel.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelConstraint.ConstraintModel.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintModel' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintModel'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // priority
        if (this.isPriorityDefined && this.priority !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.priority === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.priority);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.enabled === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.enabled);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintModel
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.constraintModel === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.constraintModel.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // priority
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.priority = <any>null;
                    break;
                case 1:
                    this.priority = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.enabled = <any>null;
                    break;
                case 1:
                    this.enabled = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintModel
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.constraintModel = new ModelConstraint.ConstraintModel();
                    this.constraintModel.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): LinkedConstraintModel {
        // Method Implementation: simple
        const schema = LinkedConstraintModelZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: LinkedConstraintModelZodSchemaShape): LinkedConstraintModel {
        // Method Implementation: parseZodSchema
        return new LinkedConstraintModel({
            name: schema.name,
            priority: schema.priority,
            enabled: schema.enabled,
            constraintModel: schema.constraintModel && ModelConstraint.ConstraintModel.parseZodSchema(schema.constraintModel)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            priority: this.isPriorityDefined ? ((this.priority === null ? undefined : this.priority)) : undefined,
            enabled: this.isEnabledDefined ? ((this.enabled === null ? undefined : this.enabled)) : undefined,
            constraintModel: this.isConstraintModelDefined ? (this.constraintModel) : undefined,
        };
    }

    public clone(): LinkedConstraintModel {
        // Method Implementation: clone
        return new LinkedConstraintModel({
            name: this.isNameDefined ? this.name : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            constraintModel: this.isConstraintModelDefined ? this.constraintModel.clone() : undefined
        });
    }
}

export abstract class AChild implements IChild {
    public static readonly modelName: string = 'Child';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'type', 'mapper', 'variables' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AChild.modelName;
    }

    private _name: string;

    private _type: string;

    private _mapper: ModelMapping.Mapper;

    private _variables: ChildVariable[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get type(): string {
        // Method Implementation: simple
        return this._type;
    }

    public get mapper(): ModelMapping.Mapper {
        // Method Implementation: simple
        return this._mapper;
    }

    public get variables(): ChildVariable[] {
        // Method Implementation: simple
        return this._variables;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set type(value: string) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set mapper(value: ModelMapping.Mapper) {
        // Method Implementation: simple
        this._mapper = value;
        this._isMapperDefined = true;
    }

    public set variables(value: ChildVariable[]) {
        // Method Implementation: simple
        this._variables = value;
        this._isVariablesDefined = true;
    }

    private _isNameDefined: boolean;

    private _isTypeDefined: boolean;

    private _isMapperDefined: boolean;

    private _isVariablesDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isMapperDefined(): boolean {
        // Method Implementation: simple
        return this._isMapperDefined;
    }

    public get isVariablesDefined(): boolean {
        // Method Implementation: simple
        return this._isVariablesDefined;
    }

    public constructor();
    public constructor(source: IChild);
    public constructor(source: Partial<Pick<AChild, 'name' | 'type' | 'mapper' | 'variables'>>);
    public constructor(source?: IChild | Partial<Pick<AChild, 'name' | 'type' | 'mapper' | 'variables'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isChildModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
                
                if (source.isMapperDefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
                
                if (source.isVariablesDefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
                
                if (source.mapper !== undefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
                
                if (source.variables !== undefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._type = '';
            this._isTypeDefined = false;
        
            this._mapper = new ModelMapping.Mapper();
            this._isMapperDefined = true;
        
            this._variables = [];
            this._isVariablesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AChild.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isMapperDefined && this.mapper !== undefined)
            definedProperties.push('mapper');
        if (this.isVariablesDefined && this.variables !== undefined)
            definedProperties.push('variables');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'type':
                return this.type;
            case 'mapper':
                return this.mapper;
            case 'variables':
                return this.variables;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isMapperDefined && this.mapper !== undefined) {
            if (this.mapper !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.mapper.getModelName(), this.mapper.getDefinedPropertyNames().length));
                this.mapper.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'null'));
            }
        }
        
        if (this.isVariablesDefined && this.variables !== undefined) {
            if (this.variables !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.variables.length));
                for (let i = 0; i < this.variables.length; i++) {
                    const item = this.variables[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.type = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'type' cannot be null`);
                    this.type = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'mapper':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ModelMapping.Mapper.modelName) {
                        this.mapper = new ModelMapping.Mapper();
                        this.mapper.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelMapping.Mapper.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'mapper' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mapper'`);
                }
        
                return true;
            case 'variables':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.variables = [];
                
                    let item: ChildVariable = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ChildVariable.modelName) {
                                item = new ChildVariable();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ChildVariable.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'variables' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'variables'`);
                        }
                
                        this.variables.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'variables' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variables'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.type === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.type);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // mapper
        if (this.isMapperDefined && this.mapper !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.mapper === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.mapper.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // variables
        if (this.isVariablesDefined && this.variables !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.variables === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.variables.length);
            
                for (const item of this.variables) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.type = <any>null;
                    break;
                case 1:
                    this.type = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // mapper
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.mapper = new ModelMapping.Mapper();
                    this.mapper.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // variables
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ChildVariable;
            
                    this.variables = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ChildVariable();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.variables.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AChild {
        // Method Implementation: simple
        const schema = ChildZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ChildZodSchemaShape): AChild {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.childType)
        {
            case 'inlineChild':
            case 'inline':
                return InlineChild.parse(schema);
            case 'referenceChild':
            case 'reference':
                return ReferenceChild.parse(schema);
            default:
                throw new Error(`childType of ${ schema.childType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            type: this.isTypeDefined ? ((this.type === null ? undefined : this.type)) : undefined,
            mapper: this.isMapperDefined ? (this.mapper) : undefined,
            variables: this.isVariablesDefined ? (this.variables?.map(item => item)) : undefined,
        };
    }

    public abstract clone(): AChild;
}

export class InlineChild extends AChild implements IInlineChild {
    public static readonly modelName: string = 'InlineChild';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entity' ];

    public get modelName(): string {
        // Method Implementation: simple
        return InlineChild.modelName;
    }

    private _entity: Entity;

    public get entity(): Entity {
        // Method Implementation: simple
        return this._entity;
    }

    public set entity(value: Entity) {
        // Method Implementation: simple
        this._entity = value;
        this._isEntityDefined = true;
    }

    private _isEntityDefined: boolean;

    public get isEntityDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityDefined;
    }

    public constructor();
    public constructor(source: IInlineChild);
    public constructor(source: Partial<Pick<InlineChild, 'entity' | 'name' | 'type' | 'mapper' | 'variables'>>);
    public constructor(source?: IInlineChild | Partial<Pick<InlineChild, 'entity' | 'name' | 'type' | 'mapper' | 'variables'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isInlineChildModel(source)) {
                if (source.isEntityDefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = new Entity();
                    this._isEntityDefined = true;
                }
            }
            else {
                if (source.entity !== undefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = new Entity();
                    this._isEntityDefined = true;
                }
            }
        }
        else {
            super();
        
            this._entity = new Entity();
            this._isEntityDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...InlineChild.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isEntityDefined && this.entity !== undefined)
            definedProperties.push('entity');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entity':
                return this.entity;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isEntityDefined && this.entity !== undefined) {
            if (this.entity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.entity.getModelName(), this.entity.getDefinedPropertyNames().length));
                this.entity.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entity':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === Entity.modelName) {
                        this.entity = new Entity();
                        this.entity.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Entity.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'entity' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entity'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // entity
        if (this.isEntityDefined && this.entity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.entity === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.entity.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // entity
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.entity = new Entity();
                    this.entity.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): InlineChild {
        // Method Implementation: simple
        const schema = InlineChildZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: InlineChildZodSchemaShape): InlineChild {
        // Method Implementation: parseZodSchema
        return new InlineChild({
            name: schema.name,
            type: schema.type,
            mapper: schema.mapper && ModelMapping.Mapper.parseZodSchema(schema.mapper),
            variables: schema.variables?.map(i => ChildVariable.parseZodSchema(i)),
            entity: schema.entity && Entity.parseZodSchema(schema.entity)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            childType: 'inline',
            ...super.toJSON(),
            entity: this.isEntityDefined ? (this.entity) : undefined,
        };
    }

    public clone(): InlineChild {
        // Method Implementation: clone
        return new InlineChild({
            name: this.isNameDefined ? this.name : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            mapper: this.isMapperDefined ? this.mapper.clone() : undefined,
            variables: this.isVariablesDefined ? this.variables?.map(i => i.clone()) : undefined,
            entity: this.isEntityDefined ? this.entity.clone() : undefined
        });
    }
}

export class ReferenceChild extends AChild implements IReferenceChild {
    public static readonly modelName: string = 'ReferenceChild';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entity' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ReferenceChild.modelName;
    }

    private _entity: string;

    public get entity(): string {
        // Method Implementation: simple
        return this._entity;
    }

    public set entity(value: string) {
        // Method Implementation: simple
        this._entity = value;
        this._isEntityDefined = true;
    }

    private _isEntityDefined: boolean;

    public get isEntityDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityDefined;
    }

    public constructor();
    public constructor(source: IReferenceChild);
    public constructor(source: Partial<Pick<ReferenceChild, 'entity' | 'name' | 'type' | 'mapper' | 'variables'>>);
    public constructor(source?: IReferenceChild | Partial<Pick<ReferenceChild, 'entity' | 'name' | 'type' | 'mapper' | 'variables'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isReferenceChildModel(source)) {
                if (source.isEntityDefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = '';
                    this._isEntityDefined = false;
                }
            }
            else {
                if (source.entity !== undefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = '';
                    this._isEntityDefined = false;
                }
            }
        }
        else {
            super();
        
            this._entity = '';
            this._isEntityDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...ReferenceChild.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isEntityDefined && this.entity !== undefined)
            definedProperties.push('entity');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entity':
                return this.entity;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isEntityDefined && this.entity !== undefined) {
            if (this.entity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entity);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entity':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entity = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entity' cannot be null`);
                    this.entity = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entity'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // entity
        if (this.isEntityDefined && this.entity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entity === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entity);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // entity
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entity = <any>null;
                    break;
                case 1:
                    this.entity = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ReferenceChild {
        // Method Implementation: simple
        const schema = ReferenceChildZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ReferenceChildZodSchemaShape): ReferenceChild {
        // Method Implementation: parseZodSchema
        return new ReferenceChild({
            name: schema.name,
            type: schema.type,
            mapper: schema.mapper && ModelMapping.Mapper.parseZodSchema(schema.mapper),
            variables: schema.variables?.map(i => ChildVariable.parseZodSchema(i)),
            entity: schema.entity
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            childType: 'reference',
            ...super.toJSON(),
            entity: this.isEntityDefined ? ((this.entity === null ? undefined : this.entity)) : undefined,
        };
    }

    public clone(): ReferenceChild {
        // Method Implementation: clone
        return new ReferenceChild({
            name: this.isNameDefined ? this.name : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            mapper: this.isMapperDefined ? this.mapper.clone() : undefined,
            variables: this.isVariablesDefined ? this.variables?.map(i => i.clone()) : undefined,
            entity: this.isEntityDefined ? this.entity : undefined
        });
    }
}

export class ChildVariable implements IChildVariable {
    public static readonly modelName: string = 'ChildVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ChildVariable.modelName;
    }

    private _name: string;

    private _value: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isNameDefined: boolean;

    private _isValueDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IChildVariable);
    public constructor(source: Partial<Pick<ChildVariable, 'name' | 'value'>>);
    public constructor(source?: IChildVariable | Partial<Pick<ChildVariable, 'name' | 'value'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isChildVariableModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ChildVariable.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'value':
                return this.value;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ChildVariable {
        // Method Implementation: simple
        const schema = ChildVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ChildVariableZodSchemaShape): ChildVariable {
        // Method Implementation: parseZodSchema
        return new ChildVariable({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): ChildVariable {
        // Method Implementation: clone
        return new ChildVariable({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export abstract class AVariable implements IVariable {
    public static readonly modelName: string = 'Variable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'value', 'required', 'strict', 'validators', 'assignmentGroup' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AVariable.modelName;
    }

    private _name: string;

    private _value: string;

    private _required: string;

    private _strict: string;

    private _validators: Validator[];

    private _assignmentGroup: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public get required(): string {
        // Method Implementation: simple
        return this._required;
    }

    public get strict(): string {
        // Method Implementation: simple
        return this._strict;
    }

    public get validators(): Validator[] {
        // Method Implementation: simple
        return this._validators;
    }

    public get assignmentGroup(): string {
        // Method Implementation: simple
        return this._assignmentGroup;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set required(value: string) {
        // Method Implementation: simple
        this._required = value;
        this._isRequiredDefined = true;
    }

    public set strict(value: string) {
        // Method Implementation: simple
        this._strict = value;
        this._isStrictDefined = true;
    }

    public set validators(value: Validator[]) {
        // Method Implementation: simple
        this._validators = value;
        this._isValidatorsDefined = true;
    }

    public set assignmentGroup(value: string) {
        // Method Implementation: simple
        this._assignmentGroup = value;
        this._isAssignmentGroupDefined = true;
    }

    private _isNameDefined: boolean;

    private _isValueDefined: boolean;

    private _isRequiredDefined: boolean;

    private _isStrictDefined: boolean;

    private _isValidatorsDefined: boolean;

    private _isAssignmentGroupDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isRequiredDefined(): boolean {
        // Method Implementation: simple
        return this._isRequiredDefined;
    }

    public get isStrictDefined(): boolean {
        // Method Implementation: simple
        return this._isStrictDefined;
    }

    public get isValidatorsDefined(): boolean {
        // Method Implementation: simple
        return this._isValidatorsDefined;
    }

    public get isAssignmentGroupDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignmentGroupDefined;
    }

    public constructor();
    public constructor(source: IVariable);
    public constructor(source: Partial<Pick<AVariable, 'name' | 'value' | 'required' | 'strict' | 'validators' | 'assignmentGroup'>>);
    public constructor(source?: IVariable | Partial<Pick<AVariable, 'name' | 'value' | 'required' | 'strict' | 'validators' | 'assignmentGroup'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isVariableModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isRequiredDefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = '';
                    this._isRequiredDefined = false;
                }
                
                if (source.isStrictDefined) {
                    this._strict = source.strict;
                    this._isStrictDefined = true;
                }
                else {
                    this._strict = '';
                    this._isStrictDefined = false;
                }
                
                if (source.isValidatorsDefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.isAssignmentGroupDefined) {
                    this._assignmentGroup = source.assignmentGroup;
                    this._isAssignmentGroupDefined = true;
                }
                else {
                    this._assignmentGroup = '';
                    this._isAssignmentGroupDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.required !== undefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = '';
                    this._isRequiredDefined = false;
                }
                
                if (source.strict !== undefined) {
                    this._strict = source.strict;
                    this._isStrictDefined = true;
                }
                else {
                    this._strict = '';
                    this._isStrictDefined = false;
                }
                
                if (source.validators !== undefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.assignmentGroup !== undefined) {
                    this._assignmentGroup = source.assignmentGroup;
                    this._isAssignmentGroupDefined = true;
                }
                else {
                    this._assignmentGroup = '';
                    this._isAssignmentGroupDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._value = '';
            this._isValueDefined = false;
        
            this._required = '';
            this._isRequiredDefined = false;
        
            this._strict = '';
            this._isStrictDefined = false;
        
            this._validators = [];
            this._isValidatorsDefined = true;
        
            this._assignmentGroup = '';
            this._isAssignmentGroupDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AVariable.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isRequiredDefined && this.required !== undefined)
            definedProperties.push('required');
        if (this.isStrictDefined && this.strict !== undefined)
            definedProperties.push('strict');
        if (this.isValidatorsDefined && this.validators !== undefined)
            definedProperties.push('validators');
        if (this.isAssignmentGroupDefined && this.assignmentGroup !== undefined)
            definedProperties.push('assignmentGroup');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'value':
                return this.value;
            case 'required':
                return this.required;
            case 'strict':
                return this.strict;
            case 'validators':
                return this.validators;
            case 'assignmentGroup':
                return this.assignmentGroup;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isRequiredDefined && this.required !== undefined) {
            if (this.required !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.required);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'null'));
            }
        }
        
        if (this.isStrictDefined && this.strict !== undefined) {
            if (this.strict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('strict', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.strict);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('strict', 'null'));
            }
        }
        
        if (this.isValidatorsDefined && this.validators !== undefined) {
            if (this.validators !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validators.length));
                for (let i = 0; i < this.validators.length; i++) {
                    const item = this.validators[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'null'));
            }
        }
        
        if (this.isAssignmentGroupDefined && this.assignmentGroup !== undefined) {
            if (this.assignmentGroup !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentGroup', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.assignmentGroup);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentGroup', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'required':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.required = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'required' cannot be null`);
                    this.required = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'required'`);
                }
        
                return true;
            case 'strict':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.strict = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'strict' cannot be null`);
                    this.strict = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'strict'`);
                }
        
                return true;
            case 'validators':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validators = [];
                
                    let item: Validator = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Validator.modelName) {
                                item = new Validator();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Validator.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validators' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validators'`);
                        }
                
                        this.validators.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validators' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validators'`);
                }
        
                return true;
            case 'assignmentGroup':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.assignmentGroup = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'assignmentGroup' cannot be null`);
                    this.assignmentGroup = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignmentGroup'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // required
        if (this.isRequiredDefined && this.required !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.required === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.required);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // strict
        if (this.isStrictDefined && this.strict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.strict === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.strict);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validators
        if (this.isValidatorsDefined && this.validators !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validators === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validators.length);
            
                for (const item of this.validators) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignmentGroup
        if (this.isAssignmentGroupDefined && this.assignmentGroup !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.assignmentGroup === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.assignmentGroup);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // required
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.required = <any>null;
                    break;
                case 1:
                    this.required = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // strict
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.strict = <any>null;
                    break;
                case 1:
                    this.strict = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validators
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Validator;
            
                    this.validators = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Validator();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validators.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // assignmentGroup
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.assignmentGroup = <any>null;
                    break;
                case 1:
                    this.assignmentGroup = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AVariable {
        // Method Implementation: simple
        const schema = VariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: VariableZodSchemaShape): AVariable {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.variableType)
        {
            case 'stringVariable':
            case 'string':
                return StringVariable.parse(schema);
            case 'numericVariable':
            case 'numeric':
                return NumericVariable.parse(schema);
            case 'booleanVariable':
            case 'boolean':
                return BooleanVariable.parse(schema);
            default:
                throw new Error(`variableType of ${ schema.variableType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            required: this.isRequiredDefined ? ((this.required === null ? undefined : this.required)) : undefined,
            strict: this.isStrictDefined ? ((this.strict === null ? undefined : this.strict)) : undefined,
            validators: this.isValidatorsDefined ? (this.validators?.map(item => item)) : undefined,
            assignmentGroup: this.isAssignmentGroupDefined ? ((this.assignmentGroup === null ? undefined : this.assignmentGroup)) : undefined,
        };
    }

    public abstract clone(): AVariable;
}

export class StringVariable extends AVariable implements IStringVariable {
    public static readonly modelName: string = 'StringVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'options', 'multi' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringVariable.modelName;
    }

    private _options: StringVariableOption[];

    private _multi: boolean;

    public get options(): StringVariableOption[] {
        // Method Implementation: simple
        return this._options;
    }

    public get multi(): boolean {
        // Method Implementation: simple
        return this._multi;
    }

    public set options(value: StringVariableOption[]) {
        // Method Implementation: simple
        this._options = value;
        this._isOptionsDefined = true;
    }

    public set multi(value: boolean) {
        // Method Implementation: simple
        this._multi = value;
        this._isMultiDefined = true;
    }

    private _isOptionsDefined: boolean;

    private _isMultiDefined: boolean;

    public get isOptionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionsDefined;
    }

    public get isMultiDefined(): boolean {
        // Method Implementation: simple
        return this._isMultiDefined;
    }

    public constructor();
    public constructor(source: IStringVariable);
    public constructor(source: Partial<Pick<StringVariable, 'options' | 'multi' | 'name' | 'value' | 'required' | 'strict' | 'validators' | 'assignmentGroup'>>);
    public constructor(source?: IStringVariable | Partial<Pick<StringVariable, 'options' | 'multi' | 'name' | 'value' | 'required' | 'strict' | 'validators' | 'assignmentGroup'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringVariableModel(source)) {
                if (source.isOptionsDefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
                
                if (source.isMultiDefined) {
                    this._multi = source.multi;
                    this._isMultiDefined = true;
                }
                else {
                    this._multi = false;
                    this._isMultiDefined = false;
                }
            }
            else {
                if (source.options !== undefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
                
                if (source.multi !== undefined) {
                    this._multi = source.multi;
                    this._isMultiDefined = true;
                }
                else {
                    this._multi = false;
                    this._isMultiDefined = false;
                }
            }
        }
        else {
            super();
        
            this._options = [];
            this._isOptionsDefined = true;
        
            this._multi = false;
            this._isMultiDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isOptionsDefined && this.options !== undefined)
            definedProperties.push('options');
        if (this.isMultiDefined && this.multi !== undefined)
            definedProperties.push('multi');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'options':
                return this.options;
            case 'multi':
                return this.multi;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isOptionsDefined && this.options !== undefined) {
            if (this.options !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.options.length));
                for (let i = 0; i < this.options.length; i++) {
                    const item = this.options[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'null'));
            }
        }
        
        if (this.isMultiDefined && this.multi !== undefined) {
            if (this.multi !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('multi', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.multi);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('multi', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'options':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.options = [];
                
                    let item: StringVariableOption = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringVariableOption.modelName) {
                                item = new StringVariableOption();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringVariableOption.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'options' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'options'`);
                        }
                
                        this.options.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'options' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'options'`);
                }
        
                return true;
            case 'multi':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.multi = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'multi' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'multi'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // options
        if (this.isOptionsDefined && this.options !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.options === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.options.length);
            
                for (const item of this.options) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // multi
        if (this.isMultiDefined && this.multi !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.multi);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // options
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringVariableOption;
            
                    this.options = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringVariableOption();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.options.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // multi
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.multi = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): StringVariable {
        // Method Implementation: simple
        const schema = StringVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringVariableZodSchemaShape): StringVariable {
        // Method Implementation: parseZodSchema
        return new StringVariable({
            name: schema.name,
            value: schema.value,
            required: schema.required,
            strict: schema.strict,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            assignmentGroup: schema.assignmentGroup,
            options: schema.options?.map(i => StringVariableOption.parseZodSchema(i)),
            multi: schema.multi
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'string',
            ...super.toJSON(),
            options: this.isOptionsDefined ? (this.options?.map(item => item)) : undefined,
            multi: this.isMultiDefined ? (this.multi) : undefined,
        };
    }

    public clone(): StringVariable {
        // Method Implementation: clone
        return new StringVariable({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            assignmentGroup: this.isAssignmentGroupDefined ? this.assignmentGroup : undefined,
            options: this.isOptionsDefined ? this.options?.map(i => i.clone()) : undefined,
            multi: this.isMultiDefined ? this.multi : undefined
        });
    }
}

export class NumericVariable extends AVariable implements INumericVariable {
    public static readonly modelName: string = 'NumericVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'precision', 'min', 'max', 'options', 'multi' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericVariable.modelName;
    }

    private _precision: number;

    private _min: string;

    private _max: string;

    private _options: NumericVariableOption[];

    private _multi: boolean;

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): string {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): string {
        // Method Implementation: simple
        return this._max;
    }

    public get options(): NumericVariableOption[] {
        // Method Implementation: simple
        return this._options;
    }

    public get multi(): boolean {
        // Method Implementation: simple
        return this._multi;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: string) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: string) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    public set options(value: NumericVariableOption[]) {
        // Method Implementation: simple
        this._options = value;
        this._isOptionsDefined = true;
    }

    public set multi(value: boolean) {
        // Method Implementation: simple
        this._multi = value;
        this._isMultiDefined = true;
    }

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    private _isOptionsDefined: boolean;

    private _isMultiDefined: boolean;

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public get isOptionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionsDefined;
    }

    public get isMultiDefined(): boolean {
        // Method Implementation: simple
        return this._isMultiDefined;
    }

    public constructor();
    public constructor(source: INumericVariable);
    public constructor(source: Partial<Pick<NumericVariable, 'precision' | 'min' | 'max' | 'options' | 'multi' | 'name' | 'value' | 'required' | 'strict' | 'validators' | 'assignmentGroup'>>);
    public constructor(source?: INumericVariable | Partial<Pick<NumericVariable, 'precision' | 'min' | 'max' | 'options' | 'multi' | 'name' | 'value' | 'required' | 'strict' | 'validators' | 'assignmentGroup'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericVariableModel(source)) {
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = '';
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = '';
                    this._isMaxDefined = false;
                }
                
                if (source.isOptionsDefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
                
                if (source.isMultiDefined) {
                    this._multi = source.multi;
                    this._isMultiDefined = true;
                }
                else {
                    this._multi = false;
                    this._isMultiDefined = false;
                }
            }
            else {
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = '';
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = '';
                    this._isMaxDefined = false;
                }
                
                if (source.options !== undefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
                
                if (source.multi !== undefined) {
                    this._multi = source.multi;
                    this._isMultiDefined = true;
                }
                else {
                    this._multi = false;
                    this._isMultiDefined = false;
                }
            }
        }
        else {
            super();
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = '';
            this._isMinDefined = false;
        
            this._max = '';
            this._isMaxDefined = false;
        
            this._options = [];
            this._isOptionsDefined = true;
        
            this._multi = false;
            this._isMultiDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        if (this.isOptionsDefined && this.options !== undefined)
            definedProperties.push('options');
        if (this.isMultiDefined && this.multi !== undefined)
            definedProperties.push('multi');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            case 'options':
                return this.options;
            case 'multi':
                return this.multi;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
        
        if (this.isOptionsDefined && this.options !== undefined) {
            if (this.options !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.options.length));
                for (let i = 0; i < this.options.length; i++) {
                    const item = this.options[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'null'));
            }
        }
        
        if (this.isMultiDefined && this.multi !== undefined) {
            if (this.multi !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('multi', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.multi);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('multi', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.min = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'min' cannot be null`);
                    this.min = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.max = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'max' cannot be null`);
                    this.max = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            case 'options':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.options = [];
                
                    let item: NumericVariableOption = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericVariableOption.modelName) {
                                item = new NumericVariableOption();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericVariableOption.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'options' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'options'`);
                        }
                
                        this.options.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'options' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'options'`);
                }
        
                return true;
            case 'multi':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.multi = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'multi' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'multi'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.min === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.min);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.max === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.max);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // options
        if (this.isOptionsDefined && this.options !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.options === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.options.length);
            
                for (const item of this.options) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // multi
        if (this.isMultiDefined && this.multi !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.multi);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.min = <any>null;
                    break;
                case 1:
                    this.min = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.max = <any>null;
                    break;
                case 1:
                    this.max = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // options
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericVariableOption;
            
                    this.options = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericVariableOption();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.options.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // multi
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.multi = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): NumericVariable {
        // Method Implementation: simple
        const schema = NumericVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericVariableZodSchemaShape): NumericVariable {
        // Method Implementation: parseZodSchema
        return new NumericVariable({
            name: schema.name,
            value: schema.value,
            required: schema.required,
            strict: schema.strict,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            assignmentGroup: schema.assignmentGroup,
            precision: schema.precision,
            min: schema.min,
            max: schema.max,
            options: schema.options?.map(i => NumericVariableOption.parseZodSchema(i)),
            multi: schema.multi
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'numeric',
            ...super.toJSON(),
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? ((this.min === null ? undefined : this.min)) : undefined,
            max: this.isMaxDefined ? ((this.max === null ? undefined : this.max)) : undefined,
            options: this.isOptionsDefined ? (this.options?.map(item => item)) : undefined,
            multi: this.isMultiDefined ? (this.multi) : undefined,
        };
    }

    public clone(): NumericVariable {
        // Method Implementation: clone
        return new NumericVariable({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            assignmentGroup: this.isAssignmentGroupDefined ? this.assignmentGroup : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined,
            options: this.isOptionsDefined ? this.options?.map(i => i.clone()) : undefined,
            multi: this.isMultiDefined ? this.multi : undefined
        });
    }
}

export class BooleanVariable extends AVariable implements IBooleanVariable {
    public static readonly modelName: string = 'BooleanVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [  ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanVariable.modelName;
    }

    public constructor();
    public constructor(source: IBooleanVariable);
    public constructor(source: Partial<Pick<BooleanVariable, 'name' | 'value' | 'required' | 'strict' | 'validators' | 'assignmentGroup'>>);
    public constructor(source?: IBooleanVariable | Partial<Pick<BooleanVariable, 'name' | 'value' | 'required' | 'strict' | 'validators' | 'assignmentGroup'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanVariableModel(source)) {
            }
            else {
            }
        }
        else {
            super();
        
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
    }

    public static parse(hash: unknown): BooleanVariable {
        // Method Implementation: simple
        const schema = BooleanVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanVariableZodSchemaShape): BooleanVariable {
        // Method Implementation: parseZodSchema
        return new BooleanVariable({
            name: schema.name,
            value: schema.value,
            required: schema.required,
            strict: schema.strict,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            assignmentGroup: schema.assignmentGroup
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'boolean',
            ...super.toJSON(),
        };
    }

    public clone(): BooleanVariable {
        // Method Implementation: clone
        return new BooleanVariable({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            assignmentGroup: this.isAssignmentGroupDefined ? this.assignmentGroup : undefined
        });
    }
}

export abstract class AField implements IField {
    public static readonly modelName: string = 'Field';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'source', 'required', 'validators' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AField.modelName;
    }

    private _name: string;

    private _source: string;

    private _required: string;

    private _validators: Validator[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get required(): string {
        // Method Implementation: simple
        return this._required;
    }

    public get validators(): Validator[] {
        // Method Implementation: simple
        return this._validators;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set required(value: string) {
        // Method Implementation: simple
        this._required = value;
        this._isRequiredDefined = true;
    }

    public set validators(value: Validator[]) {
        // Method Implementation: simple
        this._validators = value;
        this._isValidatorsDefined = true;
    }

    private _isNameDefined: boolean;

    private _isSourceDefined: boolean;

    private _isRequiredDefined: boolean;

    private _isValidatorsDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isRequiredDefined(): boolean {
        // Method Implementation: simple
        return this._isRequiredDefined;
    }

    public get isValidatorsDefined(): boolean {
        // Method Implementation: simple
        return this._isValidatorsDefined;
    }

    public constructor();
    public constructor(source: IField);
    public constructor(source: Partial<Pick<AField, 'name' | 'source' | 'required' | 'validators'>>);
    public constructor(source?: IField | Partial<Pick<AField, 'name' | 'source' | 'required' | 'validators'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isFieldModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isRequiredDefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = '';
                    this._isRequiredDefined = false;
                }
                
                if (source.isValidatorsDefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.required !== undefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = '';
                    this._isRequiredDefined = false;
                }
                
                if (source.validators !== undefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        
            this._required = '';
            this._isRequiredDefined = false;
        
            this._validators = [];
            this._isValidatorsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AField.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isRequiredDefined && this.required !== undefined)
            definedProperties.push('required');
        if (this.isValidatorsDefined && this.validators !== undefined)
            definedProperties.push('validators');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'source':
                return this.source;
            case 'required':
                return this.required;
            case 'validators':
                return this.validators;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isRequiredDefined && this.required !== undefined) {
            if (this.required !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.required);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'null'));
            }
        }
        
        if (this.isValidatorsDefined && this.validators !== undefined) {
            if (this.validators !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validators.length));
                for (let i = 0; i < this.validators.length; i++) {
                    const item = this.validators[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'required':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.required = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'required' cannot be null`);
                    this.required = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'required'`);
                }
        
                return true;
            case 'validators':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validators = [];
                
                    let item: Validator = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Validator.modelName) {
                                item = new Validator();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Validator.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validators' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validators'`);
                        }
                
                        this.validators.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validators' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validators'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // required
        if (this.isRequiredDefined && this.required !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.required === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.required);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validators
        if (this.isValidatorsDefined && this.validators !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validators === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validators.length);
            
                for (const item of this.validators) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // required
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.required = <any>null;
                    break;
                case 1:
                    this.required = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validators
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Validator;
            
                    this.validators = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Validator();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validators.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AField {
        // Method Implementation: simple
        const schema = FieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: FieldZodSchemaShape): AField {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.fieldType)
        {
            case 'stringField':
            case 'string':
                return StringField.parse(schema);
            case 'numericField':
            case 'numeric':
                return NumericField.parse(schema);
            case 'booleanField':
            case 'boolean':
                return BooleanField.parse(schema);
            default:
                throw new Error(`fieldType of ${ schema.fieldType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            required: this.isRequiredDefined ? ((this.required === null ? undefined : this.required)) : undefined,
            validators: this.isValidatorsDefined ? (this.validators?.map(item => item)) : undefined,
        };
    }

    public abstract clone(): AField;
}

export class StringField extends AField implements IStringField {
    public static readonly modelName: string = 'StringField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'options' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringField.modelName;
    }

    private _options: StringFieldOption[];

    public get options(): StringFieldOption[] {
        // Method Implementation: simple
        return this._options;
    }

    public set options(value: StringFieldOption[]) {
        // Method Implementation: simple
        this._options = value;
        this._isOptionsDefined = true;
    }

    private _isOptionsDefined: boolean;

    public get isOptionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionsDefined;
    }

    public constructor();
    public constructor(source: IStringField);
    public constructor(source: Partial<Pick<StringField, 'options' | 'name' | 'source' | 'required' | 'validators'>>);
    public constructor(source?: IStringField | Partial<Pick<StringField, 'options' | 'name' | 'source' | 'required' | 'validators'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringFieldModel(source)) {
                if (source.isOptionsDefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
            else {
                if (source.options !== undefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
        }
        else {
            super();
        
            this._options = [];
            this._isOptionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isOptionsDefined && this.options !== undefined)
            definedProperties.push('options');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'options':
                return this.options;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isOptionsDefined && this.options !== undefined) {
            if (this.options !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.options.length));
                for (let i = 0; i < this.options.length; i++) {
                    const item = this.options[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'options':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.options = [];
                
                    let item: StringFieldOption = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringFieldOption.modelName) {
                                item = new StringFieldOption();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringFieldOption.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'options' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'options'`);
                        }
                
                        this.options.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'options' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'options'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // options
        if (this.isOptionsDefined && this.options !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.options === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.options.length);
            
                for (const item of this.options) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // options
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringFieldOption;
            
                    this.options = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringFieldOption();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.options.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringField {
        // Method Implementation: simple
        const schema = StringFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringFieldZodSchemaShape): StringField {
        // Method Implementation: parseZodSchema
        return new StringField({
            name: schema.name,
            source: schema.source,
            required: schema.required,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            options: schema.options?.map(i => StringFieldOption.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'string',
            ...super.toJSON(),
            options: this.isOptionsDefined ? (this.options?.map(item => item)) : undefined,
        };
    }

    public clone(): StringField {
        // Method Implementation: clone
        return new StringField({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            options: this.isOptionsDefined ? this.options?.map(i => i.clone()) : undefined
        });
    }
}

export class NumericField extends AField implements INumericField {
    public static readonly modelName: string = 'NumericField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'precision', 'options' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericField.modelName;
    }

    private _precision: number;

    private _options: NumericFieldOption[];

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get options(): NumericFieldOption[] {
        // Method Implementation: simple
        return this._options;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set options(value: NumericFieldOption[]) {
        // Method Implementation: simple
        this._options = value;
        this._isOptionsDefined = true;
    }

    private _isPrecisionDefined: boolean;

    private _isOptionsDefined: boolean;

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isOptionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionsDefined;
    }

    public constructor();
    public constructor(source: INumericField);
    public constructor(source: Partial<Pick<NumericField, 'precision' | 'options' | 'name' | 'source' | 'required' | 'validators'>>);
    public constructor(source?: INumericField | Partial<Pick<NumericField, 'precision' | 'options' | 'name' | 'source' | 'required' | 'validators'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericFieldModel(source)) {
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isOptionsDefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
            else {
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.options !== undefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
        }
        else {
            super();
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._options = [];
            this._isOptionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isOptionsDefined && this.options !== undefined)
            definedProperties.push('options');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'precision':
                return this.precision;
            case 'options':
                return this.options;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isOptionsDefined && this.options !== undefined) {
            if (this.options !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.options.length));
                for (let i = 0; i < this.options.length; i++) {
                    const item = this.options[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'options':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.options = [];
                
                    let item: NumericFieldOption = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericFieldOption.modelName) {
                                item = new NumericFieldOption();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericFieldOption.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'options' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'options'`);
                        }
                
                        this.options.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'options' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'options'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // options
        if (this.isOptionsDefined && this.options !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.options === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.options.length);
            
                for (const item of this.options) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // options
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericFieldOption;
            
                    this.options = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericFieldOption();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.options.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): NumericField {
        // Method Implementation: simple
        const schema = NumericFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericFieldZodSchemaShape): NumericField {
        // Method Implementation: parseZodSchema
        return new NumericField({
            name: schema.name,
            source: schema.source,
            required: schema.required,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            precision: schema.precision,
            options: schema.options?.map(i => NumericFieldOption.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'numeric',
            ...super.toJSON(),
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            options: this.isOptionsDefined ? (this.options?.map(item => item)) : undefined,
        };
    }

    public clone(): NumericField {
        // Method Implementation: clone
        return new NumericField({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            options: this.isOptionsDefined ? this.options?.map(i => i.clone()) : undefined
        });
    }
}

export class BooleanField extends AField implements IBooleanField {
    public static readonly modelName: string = 'BooleanField';

    public static readonly propertyNames: ReadonlyArray<string> = [  ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanField.modelName;
    }

    public constructor();
    public constructor(source: IBooleanField);
    public constructor(source: Partial<Pick<BooleanField, 'name' | 'source' | 'required' | 'validators'>>);
    public constructor(source?: IBooleanField | Partial<Pick<BooleanField, 'name' | 'source' | 'required' | 'validators'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanFieldModel(source)) {
            }
            else {
            }
        }
        else {
            super();
        
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
    }

    public static parse(hash: unknown): BooleanField {
        // Method Implementation: simple
        const schema = BooleanFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanFieldZodSchemaShape): BooleanField {
        // Method Implementation: parseZodSchema
        return new BooleanField({
            name: schema.name,
            source: schema.source,
            required: schema.required,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'boolean',
            ...super.toJSON(),
        };
    }

    public clone(): BooleanField {
        // Method Implementation: clone
        return new BooleanField({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined
        });
    }
}

export abstract class AVariableOption implements IVariableOption {
    public static readonly modelName: string = 'VariableOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'onSelectedActions', 'applicable' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AVariableOption.modelName;
    }

    private _name: string;

    private _onSelectedActions: TransientAction[];

    private _applicable: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get onSelectedActions(): TransientAction[] {
        // Method Implementation: simple
        return this._onSelectedActions;
    }

    public get applicable(): string {
        // Method Implementation: simple
        return this._applicable;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set onSelectedActions(value: TransientAction[]) {
        // Method Implementation: simple
        this._onSelectedActions = value;
        this._isOnSelectedActionsDefined = true;
    }

    public set applicable(value: string) {
        // Method Implementation: simple
        this._applicable = value;
        this._isApplicableDefined = true;
    }

    private _isNameDefined: boolean;

    private _isOnSelectedActionsDefined: boolean;

    private _isApplicableDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isOnSelectedActionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOnSelectedActionsDefined;
    }

    public get isApplicableDefined(): boolean {
        // Method Implementation: simple
        return this._isApplicableDefined;
    }

    public constructor();
    public constructor(source: IVariableOption);
    public constructor(source: Partial<Pick<AVariableOption, 'name' | 'onSelectedActions' | 'applicable'>>);
    public constructor(source?: IVariableOption | Partial<Pick<AVariableOption, 'name' | 'onSelectedActions' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isVariableOptionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isOnSelectedActionsDefined) {
                    this._onSelectedActions = source.onSelectedActions;
                    this._isOnSelectedActionsDefined = true;
                }
                else {
                    this._onSelectedActions = [];
                    this._isOnSelectedActionsDefined = true;
                }
                
                if (source.isApplicableDefined) {
                    this._applicable = source.applicable;
                    this._isApplicableDefined = true;
                }
                else {
                    this._applicable = '';
                    this._isApplicableDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.onSelectedActions !== undefined) {
                    this._onSelectedActions = source.onSelectedActions;
                    this._isOnSelectedActionsDefined = true;
                }
                else {
                    this._onSelectedActions = [];
                    this._isOnSelectedActionsDefined = true;
                }
                
                if (source.applicable !== undefined) {
                    this._applicable = source.applicable;
                    this._isApplicableDefined = true;
                }
                else {
                    this._applicable = '';
                    this._isApplicableDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._onSelectedActions = [];
            this._isOnSelectedActionsDefined = true;
        
            this._applicable = '';
            this._isApplicableDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AVariableOption.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isOnSelectedActionsDefined && this.onSelectedActions !== undefined)
            definedProperties.push('onSelectedActions');
        if (this.isApplicableDefined && this.applicable !== undefined)
            definedProperties.push('applicable');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'onSelectedActions':
                return this.onSelectedActions;
            case 'applicable':
                return this.applicable;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isOnSelectedActionsDefined && this.onSelectedActions !== undefined) {
            if (this.onSelectedActions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('onSelectedActions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.onSelectedActions.length));
                for (let i = 0; i < this.onSelectedActions.length; i++) {
                    const item = this.onSelectedActions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('onSelectedActions', 'null'));
            }
        }
        
        if (this.isApplicableDefined && this.applicable !== undefined) {
            if (this.applicable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('applicable', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.applicable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('applicable', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'onSelectedActions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.onSelectedActions = [];
                
                    let item: TransientAction = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === TransientAction.modelName) {
                                item = new TransientAction();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${TransientAction.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'onSelectedActions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'onSelectedActions'`);
                        }
                
                        this.onSelectedActions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'onSelectedActions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'onSelectedActions'`);
                }
        
                return true;
            case 'applicable':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.applicable = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'applicable' cannot be null`);
                    this.applicable = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'applicable'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // onSelectedActions
        if (this.isOnSelectedActionsDefined && this.onSelectedActions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.onSelectedActions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.onSelectedActions.length);
            
                for (const item of this.onSelectedActions) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // applicable
        if (this.isApplicableDefined && this.applicable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.applicable === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.applicable);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // onSelectedActions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: TransientAction;
            
                    this.onSelectedActions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new TransientAction();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.onSelectedActions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // applicable
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.applicable = <any>null;
                    break;
                case 1:
                    this.applicable = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AVariableOption {
        // Method Implementation: simple
        const schema = VariableOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: VariableOptionZodSchemaShape): AVariableOption {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.variableOptionType)
        {
            case 'stringVariableOption':
            case 'string':
                return StringVariableOption.parse(schema);
            case 'numericVariableOption':
            case 'numeric':
                return NumericVariableOption.parse(schema);
            default:
                throw new Error(`variableOptionType of ${ schema.variableOptionType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            onSelectedActions: this.isOnSelectedActionsDefined ? (this.onSelectedActions?.map(item => item)) : undefined,
            applicable: this.isApplicableDefined ? ((this.applicable === null ? undefined : this.applicable)) : undefined,
        };
    }

    public abstract clone(): AVariableOption;
}

export class StringVariableOption extends AVariableOption implements IStringVariableOption {
    public static readonly modelName: string = 'StringVariableOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringVariableOption.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringVariableOption);
    public constructor(source: Partial<Pick<StringVariableOption, 'value' | 'name' | 'onSelectedActions' | 'applicable'>>);
    public constructor(source?: IStringVariableOption | Partial<Pick<StringVariableOption, 'value' | 'name' | 'onSelectedActions' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringVariableOptionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringVariableOption.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringVariableOption {
        // Method Implementation: simple
        const schema = StringVariableOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringVariableOptionZodSchemaShape): StringVariableOption {
        // Method Implementation: parseZodSchema
        return new StringVariableOption({
            name: schema.name,
            onSelectedActions: schema.onSelectedActions?.map(i => TransientAction.parseZodSchema(i)),
            applicable: schema.applicable,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableOptionType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringVariableOption {
        // Method Implementation: clone
        return new StringVariableOption({
            name: this.isNameDefined ? this.name : undefined,
            onSelectedActions: this.isOnSelectedActionsDefined ? this.onSelectedActions?.map(i => i.clone()) : undefined,
            applicable: this.isApplicableDefined ? this.applicable : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericVariableOption extends AVariableOption implements INumericVariableOption {
    public static readonly modelName: string = 'NumericVariableOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericVariableOption.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericVariableOption);
    public constructor(source: Partial<Pick<NumericVariableOption, 'value' | 'name' | 'onSelectedActions' | 'applicable'>>);
    public constructor(source?: INumericVariableOption | Partial<Pick<NumericVariableOption, 'value' | 'name' | 'onSelectedActions' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericVariableOptionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericVariableOption.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericVariableOption {
        // Method Implementation: simple
        const schema = NumericVariableOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericVariableOptionZodSchemaShape): NumericVariableOption {
        // Method Implementation: parseZodSchema
        return new NumericVariableOption({
            name: schema.name,
            onSelectedActions: schema.onSelectedActions?.map(i => TransientAction.parseZodSchema(i)),
            applicable: schema.applicable,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableOptionType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericVariableOption {
        // Method Implementation: clone
        return new NumericVariableOption({
            name: this.isNameDefined ? this.name : undefined,
            onSelectedActions: this.isOnSelectedActionsDefined ? this.onSelectedActions?.map(i => i.clone()) : undefined,
            applicable: this.isApplicableDefined ? this.applicable : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export abstract class AFieldOption implements IFieldOption {
    public static readonly modelName: string = 'FieldOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'applicable' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AFieldOption.modelName;
    }

    private _name: string;

    private _applicable: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get applicable(): string {
        // Method Implementation: simple
        return this._applicable;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set applicable(value: string) {
        // Method Implementation: simple
        this._applicable = value;
        this._isApplicableDefined = true;
    }

    private _isNameDefined: boolean;

    private _isApplicableDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isApplicableDefined(): boolean {
        // Method Implementation: simple
        return this._isApplicableDefined;
    }

    public constructor();
    public constructor(source: IFieldOption);
    public constructor(source: Partial<Pick<AFieldOption, 'name' | 'applicable'>>);
    public constructor(source?: IFieldOption | Partial<Pick<AFieldOption, 'name' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isFieldOptionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isApplicableDefined) {
                    this._applicable = source.applicable;
                    this._isApplicableDefined = true;
                }
                else {
                    this._applicable = '';
                    this._isApplicableDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.applicable !== undefined) {
                    this._applicable = source.applicable;
                    this._isApplicableDefined = true;
                }
                else {
                    this._applicable = '';
                    this._isApplicableDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._applicable = '';
            this._isApplicableDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AFieldOption.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isApplicableDefined && this.applicable !== undefined)
            definedProperties.push('applicable');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'applicable':
                return this.applicable;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isApplicableDefined && this.applicable !== undefined) {
            if (this.applicable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('applicable', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.applicable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('applicable', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'applicable':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.applicable = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'applicable' cannot be null`);
                    this.applicable = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'applicable'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // applicable
        if (this.isApplicableDefined && this.applicable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.applicable === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.applicable);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // applicable
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.applicable = <any>null;
                    break;
                case 1:
                    this.applicable = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AFieldOption {
        // Method Implementation: simple
        const schema = FieldOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: FieldOptionZodSchemaShape): AFieldOption {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.fieldOptionType)
        {
            case 'stringFieldOption':
            case 'string':
                return StringFieldOption.parse(schema);
            case 'numericFieldOption':
            case 'numeric':
                return NumericFieldOption.parse(schema);
            default:
                throw new Error(`fieldOptionType of ${ schema.fieldOptionType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            applicable: this.isApplicableDefined ? ((this.applicable === null ? undefined : this.applicable)) : undefined,
        };
    }

    public abstract clone(): AFieldOption;
}

export class StringFieldOption extends AFieldOption implements IStringFieldOption {
    public static readonly modelName: string = 'StringFieldOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringFieldOption.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringFieldOption);
    public constructor(source: Partial<Pick<StringFieldOption, 'value' | 'name' | 'applicable'>>);
    public constructor(source?: IStringFieldOption | Partial<Pick<StringFieldOption, 'value' | 'name' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringFieldOptionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringFieldOption.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringFieldOption {
        // Method Implementation: simple
        const schema = StringFieldOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringFieldOptionZodSchemaShape): StringFieldOption {
        // Method Implementation: parseZodSchema
        return new StringFieldOption({
            name: schema.name,
            applicable: schema.applicable,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldOptionType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringFieldOption {
        // Method Implementation: clone
        return new StringFieldOption({
            name: this.isNameDefined ? this.name : undefined,
            applicable: this.isApplicableDefined ? this.applicable : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericFieldOption extends AFieldOption implements INumericFieldOption {
    public static readonly modelName: string = 'NumericFieldOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericFieldOption.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericFieldOption);
    public constructor(source: Partial<Pick<NumericFieldOption, 'value' | 'name' | 'applicable'>>);
    public constructor(source?: INumericFieldOption | Partial<Pick<NumericFieldOption, 'value' | 'name' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericFieldOptionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericFieldOption.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericFieldOption {
        // Method Implementation: simple
        const schema = NumericFieldOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericFieldOptionZodSchemaShape): NumericFieldOption {
        // Method Implementation: parseZodSchema
        return new NumericFieldOption({
            name: schema.name,
            applicable: schema.applicable,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldOptionType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericFieldOption {
        // Method Implementation: clone
        return new NumericFieldOption({
            name: this.isNameDefined ? this.name : undefined,
            applicable: this.isApplicableDefined ? this.applicable : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class Validator implements IValidator {
    public static readonly modelName: string = 'Validator';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'condition', 'messageKey', 'type' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Validator.modelName;
    }

    private _name: string;

    private _condition: string;

    private _messageKey: string;

    private _type: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get condition(): string {
        // Method Implementation: simple
        return this._condition;
    }

    public get messageKey(): string {
        // Method Implementation: simple
        return this._messageKey;
    }

    public get type(): string {
        // Method Implementation: simple
        return this._type;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set condition(value: string) {
        // Method Implementation: simple
        this._condition = value;
        this._isConditionDefined = true;
    }

    public set messageKey(value: string) {
        // Method Implementation: simple
        this._messageKey = value;
        this._isMessageKeyDefined = true;
    }

    public set type(value: string) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    private _isNameDefined: boolean;

    private _isConditionDefined: boolean;

    private _isMessageKeyDefined: boolean;

    private _isTypeDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isConditionDefined(): boolean {
        // Method Implementation: simple
        return this._isConditionDefined;
    }

    public get isMessageKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isMessageKeyDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public constructor();
    public constructor(source: IValidator);
    public constructor(source: Partial<Pick<Validator, 'name' | 'condition' | 'messageKey' | 'type'>>);
    public constructor(source?: IValidator | Partial<Pick<Validator, 'name' | 'condition' | 'messageKey' | 'type'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isValidatorModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isConditionDefined) {
                    this._condition = source.condition;
                    this._isConditionDefined = true;
                }
                else {
                    this._condition = '';
                    this._isConditionDefined = false;
                }
                
                if (source.isMessageKeyDefined) {
                    this._messageKey = source.messageKey;
                    this._isMessageKeyDefined = true;
                }
                else {
                    this._messageKey = '';
                    this._isMessageKeyDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.condition !== undefined) {
                    this._condition = source.condition;
                    this._isConditionDefined = true;
                }
                else {
                    this._condition = '';
                    this._isConditionDefined = false;
                }
                
                if (source.messageKey !== undefined) {
                    this._messageKey = source.messageKey;
                    this._isMessageKeyDefined = true;
                }
                else {
                    this._messageKey = '';
                    this._isMessageKeyDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._condition = '';
            this._isConditionDefined = false;
        
            this._messageKey = '';
            this._isMessageKeyDefined = false;
        
            this._type = '';
            this._isTypeDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Validator.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isConditionDefined && this.condition !== undefined)
            definedProperties.push('condition');
        if (this.isMessageKeyDefined && this.messageKey !== undefined)
            definedProperties.push('messageKey');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'condition':
                return this.condition;
            case 'messageKey':
                return this.messageKey;
            case 'type':
                return this.type;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isConditionDefined && this.condition !== undefined) {
            if (this.condition !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('condition', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.condition);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('condition', 'null'));
            }
        }
        
        if (this.isMessageKeyDefined && this.messageKey !== undefined) {
            if (this.messageKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messageKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.messageKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messageKey', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'condition':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.condition = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'condition' cannot be null`);
                    this.condition = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'condition'`);
                }
        
                return true;
            case 'messageKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.messageKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'messageKey' cannot be null`);
                    this.messageKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'messageKey'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.type = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'type' cannot be null`);
                    this.type = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // condition
        if (this.isConditionDefined && this.condition !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.condition === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.condition);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // messageKey
        if (this.isMessageKeyDefined && this.messageKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.messageKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.messageKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.type === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.type);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // condition
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.condition = <any>null;
                    break;
                case 1:
                    this.condition = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // messageKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.messageKey = <any>null;
                    break;
                case 1:
                    this.messageKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.type = <any>null;
                    break;
                case 1:
                    this.type = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Validator {
        // Method Implementation: simple
        const schema = ValidatorZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ValidatorZodSchemaShape): Validator {
        // Method Implementation: parseZodSchema
        return new Validator({
            name: schema.name,
            condition: schema.condition,
            messageKey: schema.messageKey,
            type: schema.type
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            condition: this.isConditionDefined ? ((this.condition === null ? undefined : this.condition)) : undefined,
            messageKey: this.isMessageKeyDefined ? ((this.messageKey === null ? undefined : this.messageKey)) : undefined,
            type: this.isTypeDefined ? ((this.type === null ? undefined : this.type)) : undefined,
        };
    }

    public clone(): Validator {
        // Method Implementation: clone
        return new Validator({
            name: this.isNameDefined ? this.name : undefined,
            condition: this.isConditionDefined ? this.condition : undefined,
            messageKey: this.isMessageKeyDefined ? this.messageKey : undefined,
            type: this.isTypeDefined ? this.type : undefined
        });
    }
}

export class TransientAction implements ITransientAction {
    public static readonly modelName: string = 'TransientAction';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'type' ];

    public get modelName(): string {
        // Method Implementation: simple
        return TransientAction.modelName;
    }

    private _name: string;

    private _type: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get type(): string {
        // Method Implementation: simple
        return this._type;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set type(value: string) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    private _isNameDefined: boolean;

    private _isTypeDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public constructor();
    public constructor(source: ITransientAction);
    public constructor(source: Partial<Pick<TransientAction, 'name' | 'type'>>);
    public constructor(source?: ITransientAction | Partial<Pick<TransientAction, 'name' | 'type'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isTransientActionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._type = '';
            this._isTypeDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return TransientAction.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'type':
                return this.type;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.type = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'type' cannot be null`);
                    this.type = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.type === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.type);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.type = <any>null;
                    break;
                case 1:
                    this.type = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): TransientAction {
        // Method Implementation: simple
        const schema = TransientActionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: TransientActionZodSchemaShape): TransientAction {
        // Method Implementation: parseZodSchema
        return new TransientAction({
            name: schema.name,
            type: schema.type
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            type: this.isTypeDefined ? ((this.type === null ? undefined : this.type)) : undefined,
        };
    }

    public clone(): TransientAction {
        // Method Implementation: clone
        return new TransientAction({
            name: this.isNameDefined ? this.name : undefined,
            type: this.isTypeDefined ? this.type : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Entity':
                return new Entity();
            case 'ConstraintModelTemplate':
                return new ConstraintModelTemplate();
            case 'InlineChildTemplate':
                return new InlineChildTemplate();
            case 'ReferenceChildTemplate':
                return new ReferenceChildTemplate();
            case 'LinkedMapper':
                return new LinkedMapper();
            case 'LinkedConstraintModel':
                return new LinkedConstraintModel();
            case 'InlineChild':
                return new InlineChild();
            case 'ReferenceChild':
                return new ReferenceChild();
            case 'ChildVariable':
                return new ChildVariable();
            case 'StringVariable':
                return new StringVariable();
            case 'NumericVariable':
                return new NumericVariable();
            case 'BooleanVariable':
                return new BooleanVariable();
            case 'StringField':
                return new StringField();
            case 'NumericField':
                return new NumericField();
            case 'BooleanField':
                return new BooleanField();
            case 'StringVariableOption':
                return new StringVariableOption();
            case 'NumericVariableOption':
                return new NumericVariableOption();
            case 'StringFieldOption':
                return new StringFieldOption();
            case 'NumericFieldOption':
                return new NumericFieldOption();
            case 'Validator':
                return new Validator();
            case 'TransientAction':
                return new TransientAction();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Entity':
                return new Entity(source);
            case 'ConstraintModelTemplate':
                return new ConstraintModelTemplate(source);
            case 'InlineChildTemplate':
                return new InlineChildTemplate(source);
            case 'ReferenceChildTemplate':
                return new ReferenceChildTemplate(source);
            case 'LinkedMapper':
                return new LinkedMapper(source);
            case 'LinkedConstraintModel':
                return new LinkedConstraintModel(source);
            case 'InlineChild':
                return new InlineChild(source);
            case 'ReferenceChild':
                return new ReferenceChild(source);
            case 'ChildVariable':
                return new ChildVariable(source);
            case 'StringVariable':
                return new StringVariable(source);
            case 'NumericVariable':
                return new NumericVariable(source);
            case 'BooleanVariable':
                return new BooleanVariable(source);
            case 'StringField':
                return new StringField(source);
            case 'NumericField':
                return new NumericField(source);
            case 'BooleanField':
                return new BooleanField(source);
            case 'StringVariableOption':
                return new StringVariableOption(source);
            case 'NumericVariableOption':
                return new NumericVariableOption(source);
            case 'StringFieldOption':
                return new StringFieldOption(source);
            case 'NumericFieldOption':
                return new NumericFieldOption(source);
            case 'Validator':
                return new Validator(source);
            case 'TransientAction':
                return new TransientAction(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Entity':
                return Entity.parse(source);
            case 'ConstraintModelTemplate':
                return ConstraintModelTemplate.parse(source);
            case 'InlineChildTemplate':
                return InlineChildTemplate.parse(source);
            case 'ReferenceChildTemplate':
                return ReferenceChildTemplate.parse(source);
            case 'LinkedMapper':
                return LinkedMapper.parse(source);
            case 'LinkedConstraintModel':
                return LinkedConstraintModel.parse(source);
            case 'InlineChild':
                return InlineChild.parse(source);
            case 'ReferenceChild':
                return ReferenceChild.parse(source);
            case 'ChildVariable':
                return ChildVariable.parse(source);
            case 'StringVariable':
                return StringVariable.parse(source);
            case 'NumericVariable':
                return NumericVariable.parse(source);
            case 'BooleanVariable':
                return BooleanVariable.parse(source);
            case 'StringField':
                return StringField.parse(source);
            case 'NumericField':
                return NumericField.parse(source);
            case 'BooleanField':
                return BooleanField.parse(source);
            case 'StringVariableOption':
                return StringVariableOption.parse(source);
            case 'NumericVariableOption':
                return NumericVariableOption.parse(source);
            case 'StringFieldOption':
                return StringFieldOption.parse(source);
            case 'NumericFieldOption':
                return NumericFieldOption.parse(source);
            case 'Validator':
                return Validator.parse(source);
            case 'TransientAction':
                return TransientAction.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
